<template>
  <div :class="{ red: isSell, green: isBuy }">
    <div class="top-panel">
      <w-order-entry-symbol-panel
        ref="topSymbolPanel"
        class="v-pad"
        :wid="wid"
        :ric="underlyingRic"
        :ticker="underlyingTicker"
        :country="underlyingMarket"
        @change="onUnderlyingChanged"
        :ticketType="ticketType"
        :ticketTypeOptions="ticketTypeOptions"
        :isSymbolEnabled="isSymbolEnabled"
        :isTicketTypeEnabled="isTicketTypeEnabled"
        @ticket-type-changed="onTicketTypeChanged"
        v-if="isTopSymbolPanelVisible"
        :eligible="eligible"
        @fractional-symbol="onFractionalSymbol"
      >
      </w-order-entry-symbol-panel>
      <div class="v-pad" v-if="strategyType">{{ strategyLegInfo }}</div>
      <v-form-checkbox
        class="hv-pad"
        v-if="isAttachProfitTakerVisible"
        :checked="profitTaker"
        @change="onProfitTakerChanged"
        >{{ i18n('labels.attachProfitTaker') }}</v-form-checkbox
      >
      <v-form-checkbox class="hv-pad" v-if="isAttachStopLossVisible" :checked="stopLoss" @change="onStopLossChanged">{{
        i18n('labels.attachStopLoss')
      }}</v-form-checkbox>

      <div class="bid-ask-panel" v-if="isBidAskPanelVisible">
        <div
          :tabindex="strategyType || isFromOrderStatusModify ? null : 0"
          class="bid-ask-value-wrapper"
          @[isClickEnabled]="onBidClicked"
          @[isKeyDownEnabled].enter="onBidClicked"
          @[isKeyDownEnabled].space="onBidClicked"
        >
          <div class="bid-ask-label">{{ i18n('labels.bid') }}</div>
          <c-tick-flash :class="getCTickFlashClass()" :value="this.bid">
            <v-fidlet :data="this.bid" type="float.price" />
          </c-tick-flash>
          <span class="sr-only" v-if="!isFromOrderStatusModify"> {{ $t('white-label.globalLabels.buttonAria') }}</span>
        </div>
        <div
          :tabindex="strategyType || isFromOrderStatusModify ? null : 0"
          class="bid-ask-value-wrapper"
          @[isClickEnabled]="onAskClicked"
          @[isKeyDownEnabled].enter="onAskClicked"
          @[isKeyDownEnabled].space="onAskClicked"
        >
          <div class="bid-ask-label">{{ i18n('labels.ask') }}</div>
          <c-tick-flash :class="getCTickFlashClass()" :value="this.ask">
            <v-fidlet :data="this.ask" type="float.price" />
          </c-tick-flash>
          <span class="sr-only" v-if="!isFromOrderStatusModify">{{ $t('white-label.globalLabels.buttonAria') }}</span>
        </div>
      </div>
    </div>
    <div class="bottom-panel" :style="{ 'flex-direction': legPanelDirection }">
      <w-order-entry-symbol-panel
        ref="bottomSymbolPanel"
        class="v-pad"
        :wid="wid"
        :ric="underlyingRic"
        :ticker="underlyingTicker"
        :country="underlyingMarket"
        @change="onUnderlyingChanged"
        :ticketType="ticketType"
        :ticketTypeOptions="ticketTypeOptions"
        :isSymbolEnabled="isSymbolEnabled"
        :isTicketTypeEnabled="isTicketTypeEnabled"
        @ticket-type-changed="onTicketTypeChanged"
        v-if="isBottomSymbolPanelVisible"
        :eligible="eligible"
        @fractional-symbol="onFractionalSymbol"
      >
      </w-order-entry-symbol-panel>
      <div class="order-entry-flex-panel v-pad">
        <c-toggle-button
          class="buy-sell-toggle-button c-order-entry"
          :label="i18n('labels.action')"
          :value="action"
          :disabled="!isActionEnabled || !isOptionTrading || isFromOrderStatusModify"
          :options="actionOptions"
          @input="onActionChanged"
          v-if="isActionToggleButtonVisible"
        />
        <v-form-group v-if="isActionInputSelectVisible" class="c-order-entry" :label="i18n('labels.action')">
          <v-form-select
            :value="action"
            :disabled="!isActionEnabled || !isOptionTrading || isFromOrderStatusModify"
            :options="eligible ? actionOptions : []"
            @input="onActionChanged"
            :aria-label="i18n('labels.action')"
          />
        </v-form-group>
        <c-order-entry-price
          class="c-order-entry"
          :label="quantityLabel"
          :value="quantity"
          :delta="quantity"
          :max="maxQuantity"
          :formatter="formatFractionalQuantity"
          :isDelta="isNotionalOrder"
          :type="fractionalQuantityType"
          :types="fractionalQuantityTypes"
          :step="priceIncrement"
          :estimatedPrice="estimatedNotional"
          :maximumFractionDigits="quantityMaximumFractionDigit"
          :isModify="isFromOrderStatusModify"
          @delta-changed="onQuantityChanged"
          @value-changed="onQuantityChanged"
          @type-changed="onFractionalTypeChanged"
          v-if="isFractionalTrade"
        />
        <c-order-entry-quantity
          class="c-order-entry"
          :ticketType="ticketType"
          :label="quantityLabel"
          :value="quantity"
          :disabled="!isQuantityEnabled || !isOptionTrading"
          :maxValue="maxQuantity"
          @input="onQuantityChanged"
          v-else
        />
        <v-form-group
          class="c-order-entry expiry"
          v-if="isExpiryStrikeOptionTypeVisible"
          :label="i18n('labels.expiry')"
        >
          <v-form-select
            :value="expiryGroupId"
            :options="expiryDateOptions"
            :disabled="!isExpiryEnabled || !isOptionTrading || isFromOrderStatusModify"
            @input="onExpiryDateChanged"
            v-b-tooltip.hover
            :title="expiryDateTooltip"
            :aria-label="i18n('labels.expiry')"
          />
        </v-form-group>
        <v-form-group
          class="c-order-entry strike-price"
          v-if="isExpiryStrikeOptionTypeVisible"
          :label="i18n('labels.strike')"
        >
          <v-form-select
            :value="strikePrice"
            :options="strikePriceOptions"
            :disabled="!isStrikeEnabled || !isOptionTrading || isFromOrderStatusModify"
            @input="onStrikePriceChanged"
            :aria-label="i18n('labels.strike')"
          />
        </v-form-group>
        <c-toggle-button
          class="c-order-entry call-put-toggle-button"
          v-if="isExpiryStrikeOptionTypeVisible"
          :value="optionType"
          :label="i18n('labels.type')"
          :options="optionTypeOptions"
          :disabled="!isOptionTypeEnabled || !isOptionTrading || isFromOrderStatusModify"
          @input="onOptionTypeChanged"
        />
      </div>
      <w-order-entry-price-panel
        :wid="wid"
        :leg="leg"
        :embedded="embedded"
        :ticketType="ticketType"
        :priceType="priceType"
        :priceTypeOptions="priceTypeOptions"
        :priceTypeEnabled="
          isUnderlyingValid && isNotLimitExt && isNotOnlyPriceType && !(isFromOrderStatusModify && isFractionalTrade)
        "
        :priceTypeVisible="!profitLossExits"
        :duration="duration"
        :durationOptions="durationOptions"
        :durationEnabled="isDurationEnabled"
        :goodTill="goodTill"
        :goodTillEnabled="isDurationEnabled"
        goodTillStart="1"
        :goodTillEnd="goodTillEnd"
        :limit="limit"
        :limitDelta="limitDelta"
        :limitType="limitType"
        :estimatedLimitPrice="estimatedLimitPrice"
        :limitEnabled="isLimitTriggerEnabled"
        :trigger="trigger"
        :triggerDelta="triggerDelta"
        :triggerType="triggerType"
        :estimatedTriggerPrice="estimatedTriggerPrice"
        :triggerEnabled="isLimitTriggerEnabled"
        :triggerVisible="isTriggerVisible"
        @value-changed="onPricePanelValueChanged"
        :priceIncrement="priceIncrement"
        :profitLossExits="profitLossExits"
        :disabled="!isOptionTrading"
        v-if="!strategyType"
      >
      </w-order-entry-price-panel>
    </div>
    <div v-if="isInsiderCheck" class="kyc">
      <b-form-checkbox class="kyc-checkbox" v-model="insider">{{ i18n('labels.insider') }}</b-form-checkbox>
      <b-form-checkbox class="kyc-checkbox" v-model="significantShareholder">{{
        i18n('labels.significantShareholder')
      }}</b-form-checkbox>
    </div>
  </div>
</template>

<script>
import CTickFlash from '@custom/CTickFlash';
import CToggleButton from '@custom/CToggleButton';
import COrderEntryQuantity from '@custom/COrderEntryQuantity';
import COrderEntryPrice from '@custom/COrderEntryPrice';
import WOrderEntryPricePanel from '@widgets/WOrderEntryWidget/WOrderEntryPricePanel';
import WOrderEntrySymbolPanel from '@widgets/WOrderEntryWidget/WOrderEntrySymbolPanel';
import orderUtils from '@widgets/WOrderEntryWidget/orderUtils.js';
import moment from 'moment';
import streamableFidsFilter from '@/utils/streamableFids';
import { API, toSymbol } from '@state/api';
import { getSymbolInfo } from '@widgets/WOrderEntryWidget/orderUtils';

export default {
  name: 'w-order-entry-leg-panel',
  mixins: [orderUtils],
  components: {
    CTickFlash,
    CToggleButton,
    COrderEntryQuantity,
    COrderEntryPrice,
    WOrderEntryPricePanel,
    WOrderEntrySymbolPanel
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    leg: {
      type: String,
      required: true
    },
    embedded: {
      type: Boolean,
      default: false
    },
    externalRic: {
      type: String,
      default: ''
    },
    conditionalOrderType: {
      type: String,
      default: '',
      required: true
    },
    profitLossExits: {
      type: Boolean,
      required: true
    },
    strategyType: {
      type: String,
      default: ''
    },
    legUpdate: {
      type: Object
    },
    fidValues: {
      type: Object
    },
    isFromOrderStatusModify: {
      type: Boolean,
      default: false
    },
    eligible: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      initialRic: null,
      nonStandardGroupId: null,
      lotSize: '-',
      maxQuantity: Infinity,
      expiryGroups: [], //list of expiry data, lotSize etc.
      fids: ['q._BID', 'q._ASK', 'q.LOT_SIZE_A'],
      subscriptionId: null,
      optionFids: {
        'xopt._STRIKE': { formatter: 'float.price' },
        'xopt._MATURITY_ISO': { formatter: null },
        'xopt._TYPE_CP': { formatter: null },
        'x.RIC': { formatter: null },
        'q.GV1_TEXT': { formatter: null }, //to capture non-standard options
        'xopt.LOT_SIZE_A': { formatter: null },
        'xopt.EXPIR_CYCL': { formatter: null }
      },
      options: null,
      expiryDateOptions: [],
      destroyed: false,
      subscribing: false,
      unsubscribing: false,
      addBlankAction: false,
      isSymbolFractional: false
    };
  },
  created() {
    const orderMode = this.$store.state.widget[this.wid].orderMode;
    if (this.$store.state.widget[this.wid].legs[this.leg] === null) {
      // Create a new leg in the store when there is none
      this.$store.commit(`widget/${this.wid}/SET_LEG_VALUES`, { [this.leg]: {} });
      this.setValues({ initialize: true, modifier: orderMode.modifier, close: orderMode.close });
      if (
        orderMode.action !== 'CREATE' &&
        orderMode.action !== 'MODIFY' &&
        this.leg === 'secondOrder' &&
        orderMode.modifier === 'OCO' &&
        orderMode.close
      ) {
        this.$store.commit(`widget/${this.wid}/CLEAR_ORDER_MODE`);
      }
    } else {
      if (this.isSymbolValid && (this.ticketType === 'option' || this.ticketType === 'strategy')) {
        this.setOptionDetailsFromRic(this.symbolRic);
      }
      this.options = null;
      if (this.profitLossExits) {
        this.setSymbol({ initialize: true });
      }
      if (orderMode && !this._.isEmpty(orderMode.action)) {
        let initialize =
          orderMode.action !== 'CREATE' && orderMode.action !== 'MODIFY' && orderMode.action !== 'COPYREVERSE';
        this.setValues({
          initialize: initialize,
          id: orderMode.action,
          modifier: orderMode.modifier,
          limit: orderMode.limit,
          close: orderMode.close
        });
        // Don't reset orderMode for created with params and modified orders and closing orders with OCO bracket
        if (
          orderMode.action !== 'MODIFY' &&
          orderMode.action !== 'CREATE' &&
          !(orderMode.modifier === 'OCO' && orderMode.close)
        ) {
          this.$store.commit(`widget/${this.wid}/CLEAR_ORDER_MODE`);
        }
      }
      this.registerSymbol(this.symbolRic).then(() => {
        this.setValues({ id: 'CREATED' });
      });
      if (this.isFromOrderStatusModify) this.maxQuantity = this.$store.state.widget[this.wid].legs[this.leg].quantity;
    }

    this.$emit('order-valid', { valid: this.isOrderValid, leg: this.leg });
  },
  beforeDestroy() {
    this.destroyed = true;
    this.streamingUnsubscribe();
  },
  computed: {
    isShortAccount() {
      let shortAccount = false;
      const accounts = this.$store.getters['accounts/getOrderEntryAccountData'];
      const selectedAccountId = this.$store.state.widget[this.wid].accountId;
      if (Array.isArray(accounts) && accounts.length && selectedAccountId) {
        const selectedAccount = accounts.find(account => account.id === String(selectedAccountId));
        if (selectedAccount) {
          const value = selectedAccount.type.stringValue;
          shortAccount = value === 'G' || value === 'H';
        }
      }
      return shortAccount;
    },
    optionTradeAuthorizationCode() {
      return this.$store.state.widget[this.wid].optionTradeAuthorizationCode;
    },
    isOptionTrading() {
      if (this.conditionalOrderType !== '') {
        if (
          (this.$store.state.widget[this.wid].legs['firstOrder'].ticketType === 'option' ||
            (this.$store.state.widget[this.wid].legs['secondOrder'] &&
              this.$store.state.widget[this.wid].legs['secondOrder'].ticketType === 'option')) &&
          this.optionTradeAuthorizationCode === 0
        ) {
          return false;
        }
      } else if (this.ticketType === 'option' && this.optionTradeAuthorizationCode === 0) {
        return false;
      }
      return true;
    },
    isFractionalTradingAllowed() {
      return this.$store.getters['accounts/isFractionalTradingAllowed'];
    },
    bid: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].bid;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/NO_PERSIST_SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'bid',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
      }
    },
    ask: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].ask;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/NO_PERSIST_SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'ask',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
      }
    },
    action: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].action;
      },
      set(value) {
        if (this.action === value) return;
        this.$store.commit(`widget/${this.wid}/SET_ACTION_VALUE`, {
          leg: this.leg,
          value: value
        });
      }
    },
    actionOptions() {
      //FIXME: This call is triggered continously -> leading to above 'action' computed set property to be called
      return this.setActionAndOptions();
    },
    duration: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].duration;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'duration', value: value });
      }
    },
    durationOptions() {
      return this.getDurationOptions();
    },
    estimatedLimitPrice() {
      const value = this.getEstimatedPrice('limit', this.leg);
      this.$store.commit(`widget/${this.wid}/NO_PERSIST_SET_LEG_VALUE`, {
        leg: this.leg,
        key: 'estimatedLimit',
        value: value
      });
      return this.getEstimatedPriceLabel(value);
    },
    estimatedTriggerPrice() {
      const value = this.getEstimatedPrice('trigger', this.leg);
      this.$store.commit(`widget/${this.wid}/NO_PERSIST_SET_LEG_VALUE`, {
        leg: this.leg,
        key: 'estimatedTrigger',
        value: value
      });
      return this.getEstimatedPriceLabel(value);
    },
    estimatedFractionalQuantity() {
      const quantity = Number.parseFloat(this.quantity);
      const base = Number.parseFloat(this.ask);
      if (quantity > 0 && base > 0) {
        return quantity / base;
      }
      return 0;
    },
    estimatedNotionalQuantity() {
      const quantity = Number.parseFloat(this.quantity);
      const base = Number.parseFloat(this.ask);
      return quantity * base;
    },
    estimatedNotional() {
      if (this.isNotionalOrder) {
        if (this.estimatedFractionalQuantity) {
          let est = this.estimatedFractionalQuantity;
          const isBillions = est > 1e9;
          if (isBillions) est = est / 1e9;
          return `${this.i18n('labels.estimate')} ${this.$f(est, 'float.fractionalQuantityFull')} ${
            isBillions ? ' Bn' : ''
          } ${this.i18n('labels.notionalShares')}`;
        } else {
          return `Est: `;
        }
      }
      return '';
    },
    expiryGroupId: {
      get() {
        if (this.$store.state.widget[this.wid].legs[this.leg]) {
          return this.$store.state.widget[this.wid].legs[this.leg].expiryGroupId;
        }
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'expiryGroupId', value: value });
      }
    },
    expiryDate: {
      get() {
        if (this.$store.state.widget[this.wid].legs[this.leg]) {
          return this.$store.state.widget[this.wid].legs[this.leg].expiryDate;
        }
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'expiryDate', value: value });
      }
    },
    currentExpiryGroup() {
      if (this.expiryGroupId && this.expiryGroups) {
        return this.expiryGroups.find(e => e.id === this.expiryGroupId);
      }
    },
    expiryDateTooltip() {
      if (this.currentExpiryGroup && this.currentExpiryGroup.isNonStandard) {
        return this.i18n('labels.nonStandard');
      }
      return '';
    },
    firstOrderAction() {
      if (this.$store.state.widget[this.wid].legs['firstOrder']) {
        return this.$store.state.widget[this.wid].legs['firstOrder'].action;
      } else {
        return '';
      }
    },
    firstOrderExpiry() {
      if (this.$store.state.widget[this.wid].legs['firstOrder']) {
        return this.$store.state.widget[this.wid].legs['firstOrder'].expiryDate;
      } else {
        return '';
      }
    },
    firstOrderPrice() {
      let firstOrderPrice = Number.NaN;
      switch (this.$store.state.widget[this.wid].legs['firstOrder'].priceType) {
        case 'market':
          {
            const firstOrderAction = this.$store.state.widget[this.wid].legs['firstOrder'].action;
            let buySellAtType;
            if (this.ticketType === 'option') {
              buySellAtType = this.$store.state.applicationSettings.orders.settings.options.buySellAtType;
            } else {
              buySellAtType = this.$store.state.applicationSettings.orders.settings.stocks.buySellAtType;
            }
            if (
              (buySellAtType === 'buyAtAsk' && this.isBuyAction(firstOrderAction)) ||
              (buySellAtType === 'buyAtBid' && this.isSellAction(firstOrderAction))
            ) {
              firstOrderPrice = this.$store.state.widget[this.wid].legs['firstOrder'].ask;
            } else {
              firstOrderPrice = this.$store.state.widget[this.wid].legs['firstOrder'].bid;
            }
          }
          break;
        case 'limit':
        case 'stopLimit':
          firstOrderPrice = this.$store.state.widget[this.wid].legs['firstOrder'].limit;
          break;
        case 'trailingStopLimit':
          firstOrderPrice = this.getEstimatedPrice('limit', 'firstOrder');
          break;
        case 'stopMarket':
          firstOrderPrice = this.$store.state.widget[this.wid].legs['firstOrder'].trigger;
          break;
        case 'trailingStopMarket':
          firstOrderPrice = this.getEstimatedPrice('trigger', 'firstOrder');
          break;
      }
      return Number.parseFloat(firstOrderPrice);
    },
    goodTill: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].goodTill;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'goodTill', value: value });
      }
    },
    goodTillEnd() {
      return this.underlyingMarket === 'CA' ? 90 : 180;
    },
    isActionEnabled() {
      if (this.profitLossExits) {
        return false;
      } else if (this.strategyType) {
        if (this.leg === 'secondOrder') {
          return this.actionOptions.length > 1;
        }
      }
      return true;
    },
    isActionInputSelectVisible() {
      if (this.profitLossExits) {
        return this.$store.state.widget[this.wid].legs['firstOrder'].ticketType === 'option';
      } else {
        return this.ticketType === 'option';
      }
    },
    isActionToggleButtonVisible() {
      if (this.profitLossExits) {
        return this.$store.state.widget[this.wid].legs['firstOrder'].ticketType === 'stock';
      } else {
        return ['stock', 'fractional'].includes(this.ticketType);
      }
    },
    isAskValid() {
      return !isNaN(this.ask);
    },
    isBidValid() {
      return !isNaN(this.bid);
    },
    isAttachProfitTakerVisible() {
      return !this.strategyType && this.profitLossExits && this.leg === 'secondOrder';
    },
    isAttachStopLossVisible() {
      return !this.strategyType && this.profitLossExits && this.leg === 'thirdOrder';
    },
    isBidAskPanelVisible() {
      return !this.embedded && !this.profitLossExits;
    },
    isBuy() {
      if (!this.action || this.isBuyAction(this.action)) {
        return true;
      }
      return false;
    },
    isOptionTypeEnabled() {
      if (this.isUnderlyingValid) {
        if (this.strategyType) {
          if (this.leg === 'firstOrder') {
            switch (this.strategyType) {
              case 'customOptionOption':
              case 'rollStrategy':
                return true;
            }
          } else if (this.leg === 'secondOrder') {
            switch (this.strategyType) {
              case 'customStockOption':
              case 'customOptionOption':
                return true;
            }
          }
        } else if (this.ticketType === 'option') {
          return true;
        }
      }
      return false;
    },
    isDebentureNoteOrRecipe() {
      return (
        this.isSymbolValid &&
        this.symbolTicker &&
        (this.symbolTicker.includes('.DB') || this.symbolTicker.includes('.NT') || this.symbolTicker.includes('.IR'))
      );
    },
    isDurationEnabled() {
      const legsShifted = this.$store.state.widget[this.wid].orderMode.legsShifted;
      if (this.leg === 'firstOrder' && !legsShifted) {
        return (
          !(this.isFromOrderStatusModify && this.duration === 'dayExt') &&
          !this.isFractionalTrade &&
          this.isUnderlyingValid
        );
      }
      return false;
    },
    isExpiryDateValid() {
      return this.expiryGroupId ? true : false;
    },
    isExpiryEnabled() {
      if (this.isUnderlyingValid && this.expiryGroups.length > 0) {
        if (this.strategyType) {
          if (this.leg === 'secondOrder') {
            switch (this.strategyType) {
              case 'bullCallSpread':
              case 'bearCallSpread':
              case 'bullPutSpread':
              case 'bearPutSpread':
              case 'callBackSpread':
              case 'callRatioSpread':
              case 'putBackSpread':
              case 'putRatioSpread':
              case 'longStraddle':
              case 'shortStraddle':
              case 'longStrangle':
              case 'shortStrangle':
              case 'longSyntheticStock':
              case 'shortSyntheticStock':
                return false;
            }
          }
        }
        return true;
      }
      return false;
    },
    isStrikeEnabled() {
      if (this.isUnderlyingValid && this.expiryGroupId && this.strikePrices.length > 0) {
        if (this.strategyType) {
          if (this.leg === 'secondOrder') {
            switch (this.strategyType) {
              case 'longStraddle':
              case 'shortStraddle':
              case 'longSyntheticStock':
              case 'shortSyntheticStock':
              case 'longCallCalendar':
              case 'shortCallCalendar':
              case 'longPutCalendar':
              case 'shortPutCalendar':
                return false;
            }
          }
        }
        return true;
      }
      return false;
    },
    isLimitDelta() {
      return this.limitType.endsWith('$') || this.limitType.endsWith('%');
    },
    isTriggerDelta() {
      return this.triggerType.endsWith('$') || this.triggerType.endsWith('%');
    },
    isExpiryStrikeOptionTypeVisible() {
      return !this.profitLossExits && this.ticketType === 'option';
    },
    isQuantityEnabled() {
      if (this.strategyType) {
        if (!this.underlyingRic) {
          return false;
        }
        let value = this.leg === 'firstOrder';
        const firstOrderAction = this.$store.state.widget[this.wid].legs['firstOrder'].action;
        switch (this.strategyType) {
          case 'callBackSpread':
          case 'putBackSpread':
            switch (firstOrderAction) {
              case 'buyToOpen':
              case 'sellToClose':
                return !value;
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
              case 'buyToClose':
                return value;
            }
            break;
          case 'callRatioSpread':
          case 'putRatioSpread':
            switch (firstOrderAction) {
              case 'buyToOpen':
              case 'sellToClose':
                return value;
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
              case 'buyToClose':
                return !value;
            }
            break;
        }

        if (this.leg === 'firstOrder') {
          switch (this.strategyType) {
            case 'coveredCall':
            case 'protectedPut':
              return false;
          }
        } else if (this.leg === 'secondOrder') {
          switch (this.strategyType) {
            case 'bullCallSpread':
            case 'bearCallSpread':
            case 'bullPutSpread':
            case 'bearPutSpread':
            case 'longStraddle':
            case 'shortStraddle':
            case 'longStrangle':
            case 'shortStrangle':
            case 'longSyntheticStock':
            case 'shortSyntheticStock':
            case 'longCallCalendar':
            case 'shortCallCalendar':
            case 'longPutCalendar':
            case 'shortPutCalendar':
            case 'diagonalBullCallSpread':
            case 'diagonalBearCallSpread':
            case 'diagonalBullPutSpread':
            case 'diagonalBearPutSpread':
              return false;
          }
        }
      }
      return this.isLimitTriggerEnabled;
    },
    isLimitTriggerEnabled() {
      return (
        (this.leg === 'firstOrder' && this.isUnderlyingValid) ||
        (this.leg === 'secondOrder' &&
          this.isUnderlyingValid &&
          ((this.isAttachProfitTakerVisible && this.profitTaker) || this.conditionalOrderType !== '')) ||
        this.strategyType ||
        (this.leg === 'thirdOrder' &&
          this.isUnderlyingValid &&
          ((this.isAttachStopLossVisible && this.stopLoss) || this.conditionalOrderType !== ''))
      );
    },
    isOrderValid() {
      if (this.wid && this.$store.state.widget[this.wid].legs[this.leg]) {
        if (!this.action) return false;

        const durationValid = this.duration !== 'gtd' || this.goodTill;
        const quantityValid = Number.parseFloat(this.quantity) > 0;
        const limitValid = this.isLimitDelta ? Number.parseFloat(this.limitDelta) >= 0 : Boolean(this.limit);
        const triggerValid = this.isTriggerDelta ? Number.parseFloat(this.triggerDelta) >= 0 : Boolean(this.trigger);
        if (this.profitLossExits && (this.leg === 'secondOrder' || this.leg === 'thirdOrder')) {
          if (this.leg === 'secondOrder' && this.profitTaker) {
            return Boolean(quantityValid && limitValid && durationValid);
          } else if (this.leg === 'thirdOrder' && this.stopLoss) {
            return Boolean(quantityValid && limitValid && triggerValid && durationValid);
          } else {
            return true;
          }
        }

        if (this.ticketType === 'fractional' && !this.isFractionalTradingAllowed) return false;

        if (this.symbolRic && this.symbolTicker) {
          if (this.strategyType) {
            return (
              quantityValid &&
              (this.ticketType === 'stock' ||
                (this.ticketType === 'option' && this.isExpiryDateValid && this.isStrikePriceValid))
            );
          }
          if (this.priceType === 'market') {
            return quantityValid && durationValid;
          } else if (this.priceType === 'limit') {
            return Boolean(quantityValid && limitValid && durationValid);
          } else if (this.priceType === 'stopMarket') {
            return Boolean(quantityValid && triggerValid && durationValid);
          } else if (this.priceType === 'stopLimit') {
            return Boolean(quantityValid && triggerValid && limitValid && durationValid);
          } else if (this.priceType === 'trailingStopMarket') {
            return Boolean(quantityValid && triggerValid && durationValid);
          } else if (this.priceType === 'trailingStopLimit') {
            return Boolean(quantityValid && triggerValid && limitValid && durationValid);
          }
        }
      }
      return false;
    },
    isSell() {
      if (this.action && this.isSellAction(this.action)) {
        return true;
      }
      return false;
    },
    isTopSymbolPanelVisible() {
      return !this.embedded && !this.profitLossExits && !this.strategyType;
    },
    isBottomSymbolPanelVisible() {
      return this.embedded && !this.profitLossExits && !this.strategyType;
    },
    isStrikePriceValid() {
      return this.strikePrice ? true : false;
    },
    isSymbolValid() {
      return this.symbolRic ? true : false;
    },
    isSymbolEnabled() {
      return this.isOptionTrading && !this.isFromOrderStatusModify;
    },
    isTicketTypeEnabled() {
      return (
        !this.isFromOrderStatusModify && this.conditionalOrderType !== 'oco' && this.conditionalOrderType !== 'fto'
      );
    },
    isTriggerVisible() {
      return this.profitLossExits && this.leg === 'thirdOrder';
    },
    isUnderlyingValid() {
      return this.underlyingRic ? true : false;
    },
    isNotLimitExt() {
      return !(this.isFromOrderStatusModify && this.duration === 'dayExt');
    },
    isNotOnlyPriceType() {
      return this.priceTypeOptions.length > 1;
    },
    legPanelDirection() {
      return ['stock', 'fractional'].includes(this.ticketType) || this.profitLossExits ? 'row' : 'column';
    },
    limit: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].limit;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'limit',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
        this.emitFidValues();
      }
    },
    limitDelta: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].limitDelta;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'limitDelta',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
      }
    },
    limitType: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].limitType;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'limitType', value: value });
      }
    },
    optionType: {
      get() {
        if (this.$store.state.widget[this.wid].legs[this.leg]) {
          return this.$store.state.widget[this.wid].legs[this.leg].optionType;
        }
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'optionType', value: value });
      }
    },
    optionTypeOptions() {
      return [
        { value: 'call', text: this.i18n('optionTypes.short.call'), variant: 'primary' },
        { value: 'put', text: this.i18n('optionTypes.short.put'), variant: 'primary' }
      ];
    },
    priceType: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].priceType;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'priceType', value: value });
        this.emitFidValues();
      }
    },
    priceTypeOptions() {
      return this.getPriceTypeOptions();
    },
    priceIncrement() {
      if (this.ticketType === 'stock') {
        return parseFloat(this.$store.state.applicationSettings.orders.settings.stocks.defaultPriceIncrement);
      } else if (this.isFractionalTrade) {
        return this.isNotionalOrder
          ? 0.01
          : parseFloat(this.$store.state.applicationSettings.orders.settings.stocks.defaultOrderIncrement);
      } else if (this.ticketType === 'option') {
        return parseFloat(this.$store.state.applicationSettings.orders.settings.options.defaultPriceIncrement);
      }
      return 0;
    },
    profitTaker: {
      get() {
        return this.$store.state.widget[this.wid].profitTaker;
      },
      set(value) {
        if (value === false || value === true) {
          this.$store.commit(`widget/${this.wid}/PROFIT_TAKER`, value);
        }
      }
    },
    strikePriceOptions() {
      return this.getStrikePriceOptions();
    },
    stopLoss: {
      get() {
        return this.$store.state.widget[this.wid].stopLoss;
      },
      set(value) {
        if (value === false || value === true) {
          this.$store.commit(`widget/${this.wid}/STOP_LOSS`, value);
        }
      }
    },
    strategyLegInfo() {
      let info = this.i18n('strategy.types.' + this.strategyType) + ' - ';
      if (
        this.strategyType === 'customStockOption' ||
        this.strategyType === 'coveredCall' ||
        this.strategyType === 'protectedPut'
      ) {
        if (this.leg === 'firstOrder') {
          info += this.i18n('strategy.stock');
        } else if (this.leg === 'secondOrder') {
          info += this.i18n('strategy.option');
        }
      } else {
        if (this.leg === 'firstOrder') {
          info += this.i18n('strategy.option1');
        } else if (this.leg === 'secondOrder') {
          info += this.i18n('strategy.option2');
        }
      }
      return info;
    },
    ticketTypeOptions() {
      let ticketTypeOptions = [
        { value: 'stock', short: this.i18n('ticketTypes.short.stock'), long: this.i18n('ticketTypes.long.stock') }
      ];
      if (this.leg === 'firstOrder' || this.conditionalOrderType === 'ota') {
        ticketTypeOptions.push({
          value: 'option',
          short: this.i18n('ticketTypes.short.option'),
          long: this.i18n('ticketTypes.long.option')
        });
      }
      if (this.leg === 'firstOrder') {
        ticketTypeOptions.push({
          value: 'strategy',
          short: this.i18n('ticketTypes.short.strategy'),
          long: this.i18n('ticketTypes.long.strategy')
        });
        //Check symbol eligibility
      }
      if (this.leg === 'firstOrder' && (this.isSymbolFractional || this.underlyingRic)) {
        if (this.isSymbolFractional) {
          ticketTypeOptions.push({
            value: 'fractional',
            short: this.i18n('ticketTypes.short.fractional'),
            long: this.i18n('ticketTypes.long.fractional')
          });
        }
      }
      return ticketTypeOptions;
    },
    trigger: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].trigger;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'trigger',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
        this.emitFidValues();
      }
    },
    triggerDelta: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].triggerDelta;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'triggerDelta',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
      }
    },
    triggerType: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].triggerType;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'triggerType', value: value });
      }
    },
    quantity: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].quantity;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'quantity', value: value });
      }
    },
    quantityLabel() {
      if (this.ticketType === 'option') {
        return this.i18n('labels.contracts');
      } else if (this.isDebentureNoteOrRecipe) {
        return this.i18n('labels.faceValue');
      }
      return this.i18n('labels.quantity');
    },
    strikePrice: {
      get() {
        if (this.$store.state.widget[this.wid].legs[this.leg]) {
          return this.$store.state.widget[this.wid].legs[this.leg].strikePrice;
        }
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'strikePrice',
          value: this.cleanValueForOrders(value, this.decimalSeparator)
        });
      }
    },
    strikePrices: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].strikePrices;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_STRIKE_PRICES`, { leg: this.leg, value: value });
      }
    },
    symbolRic: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg]['symbol'].ric;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_SYMBOL_VALUE`, { leg: this.leg, key: 'ric', value: value });
      }
    },
    symbolTicker: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].symbol.ticker;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_SYMBOL_VALUE`, { leg: this.leg, key: 'ticker', value: value });
      }
    },
    symbolMarket: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].symbol.market;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_SYMBOL_VALUE`, { leg: this.leg, key: 'market', value: value });
      }
    },
    ticketType: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].ticketType;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'ticketType', value: value });
      }
    },
    underlyingRic: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].underlying.ric;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_UNDERLYING_VALUE`, { leg: this.leg, key: 'ric', value: value });
      }
    },
    underlyingTicker: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].underlying.ticker;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_UNDERLYING_VALUE`, { leg: this.leg, key: 'ticker', value: value });
      }
    },
    underlyingMarket: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].underlying.market;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_UNDERLYING_VALUE`, { leg: this.leg, key: 'market', value: value });
      }
    },
    isInsiderCheck() {
      if (
        !this.profitLossExits &&
        !this.strategyType &&
        this.underlyingMarket === 'CA' &&
        this.$store.state.widget[this.wid].insiderCheck
      ) {
        return true;
      }
      return false;
    },
    insider: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].insider;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, { leg: this.leg, key: 'insider', value: value });
        if (value && this.significantShareholder) {
          this.significantShareholder = false;
        }
      }
    },
    significantShareholder: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].significantShareholder;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'significantShareholder',
          value: value
        });
        if (value && this.insider) {
          this.insider = false;
        }
      }
    },
    triggerIndFromOrderStatus() {
      return this.$store.state.widget[this.wid].legs[this.leg].triggerIndFromOrderStatus;
    },
    isClickEnabled() {
      return this.isFromOrderStatusModify || this.isFractionalTrade ? null : 'click';
    },
    isKeyDownEnabled() {
      return this.isFromOrderStatusModify || this.isFractionalTrade ? null : 'keydown';
    },
    //Fractional trading support
    isFractionalTrade() {
      return this.ticketType === 'fractional';
    },
    isNotionalOrder() {
      return this.isFractionalTrade && this.fractionalQuantityType === '$';
    },
    defaultNotionalQuantity() {
      return Number(this.$store.state.applicationSettings.orders.settings.stocks.defaultNotionalValue);
    },
    fractionalQuantityType: {
      get() {
        return this.$store.state.widget[this.wid].legs[this.leg].fractionalQuantityType;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: this.leg,
          key: 'fractionalQuantityType',
          value: value
        });
      }
    },
    fractionalQuantityTypes() {
      const types = [];
      if (this.action === 'buy') {
        types.push({
          name: this.i18n('labels.notional'),
          sr: this.i18n('labels.notional'),
          type: '$',
          icon: 'dollar-sign'
        });
      }
      types.push({ name: this.i18n('labels.quantity'), sr: this.i18n('labels.quantity'), type: '#', icon: 'hashtag' });
      return types;
    },
    quantityMaximumFractionDigit() {
      return this.isNotionalOrder ? 2 : 5;
    }
  },
  methods: {
    getEstimatedPriceLabel(value) {
      return Number.isNaN(value) ? 'Est: $-' : 'Est: $' + this.$f(value.toFixed(2), 'float.price');
    },
    calcPrice(value1, value2, type) {
      value2 = value2 ? value2 : '0';
      let result = Number.NaN;
      if (type.endsWith('$')) {
        if (this.isBuyAction(this.action)) {
          result = (Number.parseFloat(value1) - Number.parseFloat(value2)).toFixed(2);
        } else if (this.isSellAction(this.action)) {
          result = Math.abs(Number.parseFloat(value1) + Number.parseFloat(value2)).toFixed(2);
        }
      } else if (type.endsWith('%')) {
        if (this.isBuyAction(this.action)) {
          result = (Number.parseFloat(value1) * (1 - Number.parseFloat(value2) / 100.0)).toFixed(2);
        } else if (this.isSellAction(this.action)) {
          result = (Number.parseFloat(value1) * (1 + Number.parseFloat(value2) / 100.0)).toFixed(2);
        }
      }
      return result > 0 ? result : '';
    },
    clearUnderlying() {
      this.underlyingRic = this.underlyingTicker = this.underlyingMarket = '';
      if (this.$refs.topSymbolPanel) {
        this.$refs.topSymbolPanel.clear();
      }
      if (this.$refs.bottomSymbolPanel) {
        this.$refs.bottomSymbolPanel.clear();
      }
    },
    emitFidValues() {
      let price = '';
      if (this.priceType === 'market') {
        if (this.isBuyAction(this.action)) {
          price = this.ask;
        } else if (this.isSellAction(this.action)) {
          price = this.bid;
        }
      } else if (this.priceType === 'limit' || this.priceType === 'stopLimit') {
        price = this.limit;
      } else if (this.priceType === 'stopMarket') {
        price = this.trigger;
      } else if (this.priceType === 'trailingStopMarket') {
        price = this.trigger;
      } else if (this.priceType === 'trailingStopLimit') {
        price = this.limit;
      }
      price = Number.parseFloat(price);
      this.$emit('quote-update', {
        'x._PRC': price,
        'q._BID': this.bid,
        'q._ASK': this.ask,
        'q.LOT_SIZE_A': this.lotSize,
        valid: this.isBidValid && this.isAskValid
      });
    },
    configureLimit(type, value = '') {
      /**
       * Limit Type:
       * none: do nothing
       * set: set the limit once to value
       * bid: set the limit continuously to the bid value, limit is updated when bid value changes
       * bid$: set the limit to the bid value once and add value
       * bid%: set the limit to the bid value once and add value %
       * ask: set the limit continuously to the ask value, limit is updated when ask value changes
       * ask$: set the limit to the ask value once and add value
       * ask%: set the limit to the ask value once and add value %
       * $: add value to the limit
       * %: add value % to the limit
       */
      // console.log('configureLimit leg=', this.leg, 'type=', type, 'value=', value);

      this.limitType = type;
      if (type.endsWith('$') || type.endsWith('%')) {
        this.limitDelta = value;
        this.limit = '';
      } else {
        this.limitDelta = '';
        if (this.limitType === 'set') {
          this.limit = value;
          this.limitType = 'none';
        }
      }
      this.updateLimit();
    },
    configureTrigger(type, value = '') {
      /**
       * Trigger Type:
       * none: do nothing
       * set: set the trigger once to value
       * bid: set the trigger continuously to the bid value, trigger is updated when bid value changes
       * bid$: set the trigger to the bid value once and add value
       * bid%: set the trigger to the bid value once and add value %
       * ask: set the trigger continuously to the ask value, trigger is updated when ask value changes
       * ask$: set the trigger to the ask value once and add value
       * ask%: set the trigger to the ask value once and add value %
       * $: add value to the trigger
       * %: add value % to the trigger
       */
      // console.log('configureTrigger leg=', this.leg, 'type=', type, 'value=', value);

      this.triggerType = type;
      if (type.endsWith('$') || type.endsWith('%')) {
        this.triggerDelta = value;
        this.trigger = '';
      } else {
        this.triggerDelta = '';
        if (this.triggerType === 'set') {
          this.trigger = value;
          this.triggerType = 'none';
        }
      }
      this.updateTrigger();
    },
    setActionAndOptions() {
      let actions = [];
      if (this.ticketType === 'stock') {
        if (this.isShortAccount) {
          actions.push('buyToCover');
          actions.push('shortSell');
        } else {
          actions.push('buy');
          actions.push('sell');
        }
        this.action = this.getSameAction(this.action, 'stock');
      } else if (this.ticketType === 'option') {
        if (this.strategyType) {
          actions = this.getStrategyActions(this.strategyType);
        } else {
          actions = ['buyToOpen', 'buyToClose', 'sellToOpenCovered', 'sellToOpenUncovered', 'sellToClose'];
        }
        actions = this.restrictActions(actions, 'actions');
      } else if (this.ticketType === 'fractional') {
        actions.push('buy');
        actions.push('sell');
      }

      if (actions.length > 0) {
        if (this.action === '' || !actions.includes(this.action)) {
          if (this.addBlankAction) actions.unshift('');
          if (this.isShortAccount && this.ticketType === 'option' && !this.strategyType) {
            const orderMode = this.$store.state.widget[this.wid].orderMode;
            const action = orderMode.action;
            const close = orderMode.close;
            const modifier = orderMode.modifier;

            if (close) {
              if (
                action === 'COVER' &&
                actions.includes('buyToClose') &&
                (modifier === 'LIMIT' || modifier === 'STOP-LIMIT')
              )
                this.action = 'buyToClose';
            }
          } else this.action = actions[0];
        }
      }
      const isBuySellToggle = ['stock', 'fractional'].includes(this.ticketType);
      return this.toOptionArray(actions, 'actions', isBuySellToggle);
    },
    // Get possible actions for first and second order in strategies
    getStrategyActions(strategyType) {
      if (this.leg === 'firstOrder') {
        switch (strategyType) {
          case 'longStraddle':
          case 'longStrangle':
            return ['buyToOpen', 'sellToClose'];
          case 'shortStraddle':
          case 'shortStrangle':
            return ['sellToOpenUncovered', 'buyToClose'];
        }
      } else if (this.leg === 'secondOrder') {
        const firstOrderAction = this.$store.state.widget[this.wid].legs['firstOrder'].action;
        switch (strategyType) {
          case 'coveredCall':
            switch (firstOrderAction) {
              case 'buy':
              case 'buyToCover':
                return ['sellToOpenCovered', 'sellToOpenUncovered'];
              case 'sell':
              case 'shortSell':
                return ['buyToClose'];
            }
            break;
          case 'protectedPut':
            switch (firstOrderAction) {
              case 'buy':
              case 'buyToCover':
                return ['buyToOpen'];
              case 'sell':
              case 'shortSell':
                return ['sellToClose'];
            }
            break;
          case 'rollStrategy':
            switch (firstOrderAction) {
              case 'buyToOpen':
                return ['sellToClose'];
              case 'buyToClose':
                return ['sellToOpenCovered', 'sellToOpenUncovered'];
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
                return ['buyToClose'];
              case 'sellToClose':
                return ['buyToOpen'];
            }
            break;
          case 'bullCallSpread':
          case 'bearCallSpread':
          case 'bullPutSpread':
          case 'bearPutSpread':
          case 'callBackSpread':
          case 'callRatioSpread':
          case 'putBackSpread':
          case 'putRatioSpread':
          case 'longSyntheticStock':
          case 'shortSyntheticStock':
          case 'longCallCalendar':
          case 'shortCallCalendar':
          case 'longPutCalendar':
          case 'shortPutCalendar':
          case 'diagonalBullCallSpread':
          case 'diagonalBearCallSpread':
          case 'diagonalBullPutSpread':
          case 'diagonalBearPutSpread':
            switch (firstOrderAction) {
              case 'buyToOpen':
                return ['sellToOpenCovered', 'sellToOpenUncovered'];
              case 'buyToClose':
                return ['sellToClose'];
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
                return ['buyToOpen'];
              case 'sellToClose':
                return ['buyToClose'];
            }
            break;
          case 'longStraddle':
          case 'shortStraddle':
          case 'longStrangle':
          case 'shortStrangle':
            switch (firstOrderAction) {
              case 'buyToOpen':
                return ['buyToOpen'];
              case 'buyToClose':
                return ['buyToClose'];
              case 'sellToOpenUncovered':
                return ['sellToOpenUncovered'];
              case 'sellToClose':
                return ['sellToClose'];
            }
            break;
        }
      }
      return ['buyToOpen', 'buyToClose', 'sellToOpenCovered', 'sellToOpenUncovered', 'sellToClose'];
    },
    restrictActions(actions) {
      let restricted = [];
      let code = this.optionTradeAuthorizationCode;
      actions.forEach(action => {
        switch (action) {
          case 'buyToOpen':
          case 'sellToClose':
            if (code > 0) {
              restricted.push(action);
            }
            break;
          case 'buyToClose':
          case 'buyToCover':
          case 'sellToOpenCovered':
            if (code === 2 || code === 3 || code === 4) {
              restricted.push(action);
            }
            break;
          case 'sellToOpenUncovered':
            if (code === 4) {
              restricted.push(action);
            }
            break;
        }
      });
      return restricted;
    },
    getEstimatedPrice(mode, leg) {
      let price = Number.NaN;
      let delta = Number.NaN;
      let type = '';
      if (mode === 'limit') {
        type = this.$store.state.widget[this.wid].legs[leg].limitType;
        delta = Number.parseFloat(this.$store.state.widget[this.wid].legs[leg].limitDelta);
      } else if (mode === 'trigger') {
        type = this.$store.state.widget[this.wid].legs[leg].triggerType;
        delta = Number.parseFloat(this.$store.state.widget[this.wid].legs[leg].triggerDelta);
      }
      if (type === '$' || type === '%') {
        // Retrieve base value and operation type
        let base = Number.NaN;
        let operation = '';
        if (this.profitLossExits && (leg === 'secondOrder' || leg === 'thirdOrder')) {
          let action = this.$store.state.widget[this.wid].legs['firstOrder'].action;
          base = this.firstOrderPrice;
          if (
            (leg === 'secondOrder' && this.isBuyAction(action)) ||
            (leg === 'thirdOrder' && this.isSellAction(action))
          ) {
            operation = 'add';
          } else if (
            (leg === 'secondOrder' && this.isSellAction(action)) ||
            (leg === 'thirdOrder' && this.isBuyAction(action))
          ) {
            operation = 'substract';
          }
        } else {
          let action = this.$store.state.widget[this.wid].legs[leg].action;
          if (mode === 'limit') {
            base = this.getEstimatedPrice('trigger', leg);
          } else if (mode === 'trigger') {
            if (this.isBuyAction(action)) {
              base = Number.parseFloat(this.ask);
            } else if (this.isSellAction(action)) {
              base = Number.parseFloat(this.bid);
            }
          }
          if (this.isBuyAction(action)) {
            operation = 'add';
          } else if (this.isSellAction(action)) {
            operation = 'substract';
          }
        }

        // Calculate estimated price
        if (!Number.isNaN(base) && !Number.isNaN(delta)) {
          if (operation === 'add') {
            if (type === '$') {
              price = base + delta;
            } else if (type === '%') {
              price = base + (base * delta) / 100.0;
            }
          } else if (operation === 'substract') {
            if (type === '$') {
              price = base - delta;
            } else if (type === '%') {
              price = base - (base * delta) / 100.0;
            }
          }
        }
        // console.log(
        //   'getEstimatedPrice leg',
        //   leg,
        //   'mode',
        //   mode,
        //   'type',
        //   type,
        //   'base',
        //   base,
        //   'delta',
        //   delta,
        //   'operation',
        //   operation,
        //   'price',
        //   price
        // );
      }
      return Math.round(price * 100 + Number.EPSILON) / 100;
    },
    getOppositeAction(action, ticketType) {
      if (ticketType === 'stock') {
        if (this.isBuyAction(action)) {
          return this.getSellAction();
        } else if (this.isSellAction(action)) {
          return this.getBuyAction();
        }
      } else if (ticketType === 'option') {
        switch (action) {
          case 'buy':
          case 'buyToCover':
            return 'sellToClose';
          case 'buyToOpen':
            return 'sellToClose';
          case 'buyToClose':
            return 'sellToOpenCovered';
          case 'sell':
          case 'shortSell':
            return 'buyToOpen';
          case 'sellToOpenCovered':
            return 'buyToClose';
          case 'sellToOpenUncovered':
            return 'buyToClose';
          case 'sellToClose':
            return 'buyToOpen';
        }
      }
    },
    getSameAction(action, ticketType) {
      if (this.isBuyAction(action)) {
        if (ticketType === 'stock') {
          return this.getBuyAction();
        } else if (ticketType === 'option') {
          if (action === 'buy' || action === 'buyToCover') {
            return 'buyToOpen';
          } else {
            return action;
          }
        }
      } else if (this.isSellAction(action)) {
        if (ticketType === 'stock') {
          return this.getSellAction();
        } else if (ticketType === 'option') {
          if (action === 'sell' || action === 'shortSell') {
            return 'sellToClose';
          } else {
            return action;
          }
        }
      }
    },
    getStrikePriceOptions() {
      let strikePrices = [];
      if (this.strategyType) {
        if (this.leg === 'firstOrder') {
          switch (this.strategyType) {
            case 'longCallCalendar':
            case 'shortCallCalendar':
            case 'longPutCalendar':
            case 'shortPutCalendar':
              {
                let date2 = this.$store.state.widget[this.wid].legs['secondOrder'].expiryDate;
                if (date2) {
                  let prices2 = this.$store.state.widget[this.wid].legs['secondOrder'].strikePrices;
                  this.strikePrices.forEach(price1 => {
                    if (prices2.includes(price1)) {
                      strikePrices.push(price1);
                    }
                  });
                } else {
                  strikePrices = this.strikePrices;
                }
              }
              break;
            default:
              strikePrices = this.strikePrices;
          }
        } else if (this.leg === 'secondOrder') {
          let price1 = this.$store.state.widget[this.wid].legs['firstOrder'].strikePrice;
          if (price1) {
            let valid = this.strikePriceValidation();
            let containsStrikePrice = false;
            this.strikePrices.forEach(price2 => {
              if (valid(price1, price2)) {
                if (this.cleanValueForOrders(price2, this.decimalSeparator) === this.strikePrice) {
                  containsStrikePrice = true;
                }
                strikePrices.push(price2);
              }
            });
            if (!containsStrikePrice) {
              this.strikePrice = '';
            }
          } else {
            strikePrices = this.strikePrices;
          }
        }
      } else {
        strikePrices = this.strikePrices;
      }
      let strikePriceOptions = [];
      strikePrices.forEach(strikePrice => {
        strikePriceOptions.push({
          value: strikePrice,
          text: this.$f(
            this.cleanValueForOrders(strikePrice, this.decimalSeparator),
            this.optionFids['xopt._STRIKE'].formatter
          )
        });
      });
      return strikePriceOptions;
    },
    constrainExpiryDateOptions() {
      if (this.strategyType && this.leg === 'secondOrder') {
        let expiryGroups = [];
        let date1 = this.firstOrderExpiry;
        if (date1) {
          let valid = this.expiryDateValidation();
          let containsExpiryDate = false;
          this.expiryGroups.forEach(opt => {
            if (valid(date1, opt.expiryDate)) {
              if (opt.id === this.expiryGroupId || opt.expiryDate === this.expiryDate) {
                containsExpiryDate = true;
              }
              expiryGroups.push(opt);
            }
          });
          if (!containsExpiryDate) {
            this.expiryDate = '';
          }
        } else {
          expiryGroups = this.expiryGroups;
        }
        this.setExpiryDateOptions(expiryGroups);
      }
    },
    setExpiryDateOptions(expiryGroups = this.expiryGroups) {
      let expiryDateOptions = [];
      moment.locale(this.$i18n.locale);
      expiryGroups
        .filter(opt => {
          return this.options
            ? Object.keys(this.options[opt.id][this.optionType.toUpperCase().charAt(0)]).length > 0
            : true;
        })
        .forEach(opt => {
          let text = moment(opt.expiryDate, 'YYYY-MM-DD').format('MMM DD YYYY');

          if (opt.expiryType !== 'W') text += `(${opt.expiryType})`;

          if (opt.isNonStandard) {
            text += ` (${opt.lotSize})`;
          }

          expiryDateOptions.push({
            value: opt.id,
            text
          });
        });
      this.expiryDateOptions = expiryDateOptions;
    },
    getExpiryGroupId({ expiryDate, expiryType, lotSize, isNonStandard }) {
      return `${expiryDate}#${expiryType}#${isNonStandard ? 'NS#' + lotSize : 'S'}`;
    },
    getOptionChain() {
      // Fetches the option chain for a certain underlying from the REST API
      return new Promise(resolve => {
        // console.log(
        //   'getOptionChain',
        //   'leg=',
        //   this.leg,
        //   'register=',
        //   this.ticketType === 'option' && this.options === null && this.isUnderlyingValid,
        //   'underlyingRic=',
        //   this.underlyingRic,
        //   'isUnderlyingValid=',
        //   this.isUnderlyingValid,
        //   this.ticketType
        // );
        if (this.ticketType === 'option' && this.options === null && this.isUnderlyingValid && !this.profitLossExits) {
          this.options = {};
          const now = Date.now();
          // console.log('*** getOptionChain leg=', this.leg, 'underlyingRic=', this.underlyingRic);
          const fids = Object.keys(this.optionFids);
          API.Rest.get(
            `/find/options?fids=${fids}&ulname=${this.underlyingRic}&searchFor=UL_RIC&isNF=false&optionTypes=OPT`
          ).then(response => {
            this.expiryGroups = [];
            const expiryGroupMap = {};
            if (Array.isArray(response.data.data)) {
              response.data.data.forEach(data => {
                const expiryDate = data['xopt._MATURITY_ISO'];
                // Don't show past expiry dates
                if (new Date(expiryDate).getTime() >= now - 86400000) {
                  // TDWATP-8783 hardcoded exception for SPX/SPXW. Backend returns SPX weekly options marked as monthly
                  if (
                    (data['xopt.EXPIR_CYCL'].startsWith('M') || data['xopt.EXPIR_CYCL'].startsWith('E')) &&
                    data['x.RIC'].startsWith('SPXW')
                  ) {
                    data['xopt.EXPIR_CYCL'] = 'W';
                  }
                  //EOR

                  const optionType = data['xopt._TYPE_CP'];
                  const strikePrice = data['xopt._STRIKE'];
                  const lotSize = +data['xopt.LOT_SIZE_A'];
                  const gvText = data['q.GV1_TEXT'];
                  const expiryType =
                    data['xopt.EXPIR_CYCL'] !== '-'
                      ? this.getExpiryType(data['xopt.EXPIR_CYCL'])
                      : this.getExpiryTypeFromDisplayName(data['xopt._DSPLY_NAME']);

                  // optionType ,Strike price and expiryType should not be empty null or -
                  if (
                    !optionType ||
                    !strikePrice ||
                    !expiryType ||
                    !lotSize ||
                    optionType === '-' ||
                    strikePrice === '-' ||
                    expiryType === '-' ||
                    isNaN(lotSize)
                  ) {
                    return;
                  }

                  let isNonStandard = false;
                  if (!isNaN(gvText.slice('-1'))) {
                    isNonStandard = true;
                  }

                  const expiryGroupId = this.getExpiryGroupId({ expiryDate, expiryType, lotSize, isNonStandard });
                  let option;
                  if (expiryGroupId in this.options) {
                    option = this.options[expiryGroupId];
                  } else {
                    option = { C: {}, P: {} };
                    this.options[expiryGroupId] = option;
                  }
                  if (expiryDate !== '-' && !expiryGroupMap[expiryGroupId]) {
                    expiryGroupMap[expiryGroupId] = {
                      id: expiryGroupId,
                      expiryDate,
                      expiryType,
                      lotSize,
                      isNonStandard
                    };
                  }

                  if (!option[optionType][strikePrice]) {
                    option[optionType][strikePrice] = {};
                  }

                  option[optionType][strikePrice] = data;

                  if (data['x.RIC'] === this.initialRic) {
                    this.expiryGroupId = expiryGroupId;
                  }
                }
              });

              this.expiryGroups = Object.values(expiryGroupMap).sort((a, b) => {
                const compare =
                  moment(a.expiryDate, 'YYYY-MM-DD').format('YYYYMMDD') -
                  moment(b.expiryDate, 'YYYY-MM-DD').format('YYYYMMDD');
                if (compare === 0) return a.id.localeCompare(b.id);
                return compare;
              });
              this.setExpiryDateOptions();
              this.symbolRic = this.getOptionRic();
              if (this.symbolRic) {
                this.retrieveOptionDetails();
              }
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    },
    handleQuoteUpdate(data) {
      const absZero = val => {
        if (parseFloat(val) === 0) return 0; //to avoid negative zeroes
        return val;
      };
      // Don't handle updates from symbols that could not be unsubscribed previously
      if (data.length > 0 && data[0].ric === this.symbolRic) {
        const { 'q._BID': bid, 'q._ASK': ask, 'q.LOT_SIZE_A': lotSize } = data[0].data;
        this.bid = typeof bid === 'undefined' ? this.bid : this.$f(absZero(bid), 'float.price');
        this.ask = typeof ask === 'undefined' ? this.ask : this.$f(absZero(ask), 'float.price');
        this.lotSize = typeof lotSize === 'undefined' ? this.lotSize : lotSize;
      }
      this.updateLimit();
      this.updateTrigger();
      this.emitFidValues();
    },
    isBuyAction(action) {
      return action ? action.toLowerCase().includes('buy') || action.toLowerCase() === 'buyToCover' : false;
    },
    isSellAction(action) {
      return action ? action.toLowerCase().includes('sell') || action.toLowerCase() === 'shortSell' : false;
    },
    getBuyAction() {
      return this.isShortAccount ? 'buyToCover' : 'buy';
    },
    getSellAction() {
      return this.isShortAccount ? 'shortSell' : 'sell';
    },
    i18n(subkey) {
      return this.$t(`white-label.${'widgets.orderEntry'}.${subkey}`);
    },
    onActionChanged(value) {
      if (value !== this.action) {
        this.addBlankAction = false;
        let event = { id: 'ACTION_CHANGED', oldValue: this.action, newValue: value };
        this.action = value;
        this.setValues(event);
      }
    },
    onAskClicked() {
      this.configureLimit('set', this.ask);
      this.priceType = 'limit';
    },
    onBidClicked() {
      this.configureLimit('set', this.bid);
      this.priceType = 'limit';
    },
    onExpiryDateChanged(value) {
      this.expiryGroupId = value;
    },
    onOptionTypeChanged(value) {
      this.optionType = value;
    },
    onPricePanelValueChanged(arg) {
      let value = arg.value;
      switch (arg.key) {
        case 'priceType':
          if (this.priceType !== value) {
            let event = { id: 'PRICE_TYPE_CHANGED', oldValue: this.priceType, newValue: value };
            this.priceType = value;
            this.setValues(event);
          }
          break;
        case 'duration':
          if (this.duration !== value) {
            let event = { id: 'DURATION_CHANGED', oldValue: this.duration, newValue: value };
            this.duration = value;
            this.setValues(event);
          }
          break;
        case 'goodTill':
          if (this.goodTill !== value) {
            let event = { id: 'GOOD_TILL_CHANGED', oldValue: this.goodTill, newValue: value };
            this.goodTill = value;
            this.setValues(event);
          }
          break;
        case 'limit':
          this.limit = value;
          break;
        case 'limitDelta':
          this.configureLimit(this.limitType, value);
          break;
        case 'limitType':
          this.configureLimit(value, this.limitType === 'none' ? '' : this.isLimitDelta ? this.limitDelta : this.limit);
          break;
        case 'trigger':
          this.trigger = value;
          break;
        case 'triggerDelta':
          this.configureTrigger(this.triggerType, value);
          break;
        case 'triggerType':
          this.configureTrigger(
            value,
            this.triggerType === 'none' ? '' : this.isTriggerDelta ? this.triggerDelta : this.trigger
          );
          break;
      }
    },
    onProfitTakerChanged(value) {
      this.profitTaker = value;
      this.setPriceType({ initialize: true });
      if (this.ticketType === 'option' && value && this.stopLoss) {
        this.onStopLossChanged(false);
      }
    },
    onQuantityChanged(value) {
      let event = { id: 'QUANTITY_CHANGED', oldValue: this.quantity, newValue: value };
      this.quantity = value;
      this.setValues(event);
    },
    formatFractionalQuantity(val) {
      if (!val) return 0;
      let formattedValue = val;
      if (this.decimalSeparator !== '.') formattedValue = String(formattedValue).replace('.', this.decimalSeparator);
      return formattedValue;
    },
    onStopLossChanged(value) {
      this.stopLoss = value;
      this.setPriceType({ initialize: true });
      if (this.ticketType === 'option' && value && this.profitTaker) {
        this.onProfitTakerChanged(false);
      }
    },
    onStrikePriceChanged(value) {
      this.strikePrice = value;
    },
    onTicketTypeChanged(value) {
      if (this.ticketType !== value) {
        this.$emit('ticket-type-changed', value);
        this.fractionalQuantityType = this.$store.state.applicationSettings.orders.settings.stocks.defaultFractionalType;
        if (value !== 'strategy' && this.leg === 'secondOrder') {
          const event = { id: 'TICKET_TYPE_CHANGED', oldValue: this.ticketType, newValue: value };
          this.ticketType = value;
          this.setValues(event);
        }
      }
    },
    onUnderlyingChanged(event) {
      this.fractionalQuantityType = this.$store.state.applicationSettings.orders.settings.stocks.defaultFractionalType;
      let ric = event.ric.trim();
      switch (event.type) {
        case 'input':
          {
            let oldRic = this.underlyingRic;
            this.clearUnderlying();
            this.symbolRic = this.symbolTicker = this.symbolMarket = '';
            this.streamingUnsubscribe();
            this.setValues({
              id: 'UNDERLYING_CHANGED',
              oldValue: oldRic,
              newValue: ric
            });
          }
          break;
        case 'select': {
          this.underlyingMarket = event.country;
          this.options = null;
          this.configureTrigger(this.triggerType);
          this.configureLimit(this.limitType);
          if (event.ticketType === 'OPT') {
            this.underlyingRic = ric;
            getSymbolInfo(ric).then(result => {
              this.ticketType = 'option';
              this.setOptionDetailsFromRic(result.ric);
              this.underlyingRic = result.underlyingRic;
              this.registerSymbol('').then(() => {
                this.registerSymbol(result.ric);
              });
            });
          } else {
            this.underlyingRic = ric;
            this.registerSymbol(this.ticketType === 'option' ? '' : this.underlyingRic).then(() => {
              this.setValues({
                id: 'UNDERLYING_CHANGED',
                oldValue: this.underlyingRic,
                newValue: ric
              });
              console.log('Registering finished (UNDERLYING_CHANGED).');
            });
          }
          break;
        }
      }
    },
    setOptionDetailsFromRic(ric) {
      // Retrieve expiry date, strike price and option type from the symbol name
      // and display them before the option chain arrives asynchronously
      const optionDetails = this.getOptionDetails(ric);
      this.strikePrice = optionDetails.strikePrice;
      this.strikePrices = [optionDetails.strikePrice];
      this.optionType = optionDetails.optionType;
      this.initialRic = ric;
    },
    onExternalRicChanged(value) {
      if (value.ticketType) {
        this.options = null;
        this.expiryGroupId = '';
        this.strikePrices = [];
        if (value.ticketType === 'option') {
          this.setOptionDetailsFromRic(value.ric);
          this.ticketType = value.ticketType;
        } else if (value.ticketType === 'stock') {
          this.ticketType = value.ticketType;
        }
        this.underlyingRic = value.underlyingRic;
        this.setAction({ id: 'EXTERNAL_RIC' });
        this.registerSymbol(value.ticketType === 'option' ? '' : this.underlyingRic).then(() => {
          this.$emit('external-ric-registration-finished');
        });
      } else {
        this.onUnderlyingChanged({ ric: '', ticker: '', country: '', type: 'input' });
        this.$emit('external-ric-registration-finished');
      }
    },
    getOptionRic() {
      // Retrieves the option ric for a certain expiry date / strike price / option type combination
      let ric = '';
      if (this.ticketType === 'option' && !this._.isEmpty(this.options) && this.isStrikePriceValid) {
        if (
          this.options &&
          this.expiryGroupId &&
          this.optionType &&
          this.strikePrice &&
          this.options[this.expiryGroupId] &&
          this.options[this.expiryGroupId][this.optionType.toUpperCase().charAt(0)] &&
          this.options[this.expiryGroupId][this.optionType.toUpperCase().charAt(0)][this.strikePrice]
        ) {
          ric = this.options[this.expiryGroupId][this.optionType.toUpperCase().charAt(0)][this.strikePrice]['x.RIC'];
        } else {
          console.log(
            `No option found for expiry date ${this.expiryGroupId}, option type ${this.optionType
              .toUpperCase()
              .charAt(0)} and strike price ${this.strikePrice}
            }`
          );
        }
      }
      return ric;
    },
    retrieveOptionDetails() {
      if (!this._.isEmpty(this.options)) {
        if (this.isSymbolValid) {
          // Sets the expiry date, strike price and option type for an existing option ric
          const extractOptionData = (expiryGroupId, option) => {
            if (option['x.RIC'] === this.symbolRic) {
              this.expiryGroupId = expiryGroupId;
              this.strikePrice = option['xopt._STRIKE'];
              this.optionType = option['xopt._TYPE_CP'] === 'C' ? 'call' : 'put';

              if (this.strategyType === 'rollStrategy') {
                this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
                  leg: 'secondOrder',
                  key: 'optionType',
                  value: this.optionType
                });
              }
            }
          };
          Object.keys(this.options).forEach(expiryGroupId => {
            Object.keys(this.options[expiryGroupId]['C']).forEach(strikePrice => {
              extractOptionData(expiryGroupId, this.options[expiryGroupId]['C'][strikePrice]);
            });
            Object.keys(this.options[expiryGroupId]['P']).forEach(strikePrice => {
              extractOptionData(expiryGroupId, this.options[expiryGroupId]['P'][strikePrice]);
            });
          });
        }
        if (this.isExpiryDateValid) {
          // Sets the strike price for an existing expiry date and populates the strike price combo
          let strikePrices = [];
          let optionData = this.options[this.expiryGroupId][this.optionType.toUpperCase().charAt(0)];
          let containsStrikePrice = false;

          Object.keys(optionData).forEach(strikePrice => {
            strikePrices.push(strikePrice);
            if (Number.parseFloat(strikePrice) === Number.parseFloat(this.strikePrice)) {
              containsStrikePrice = true;
            }
          });

          this.strikePrices = strikePrices.sort((a, b) => a - b);
          if (!containsStrikePrice) {
            this.strikePrice = '';
          }
        }
      }
    },
    setAction(event) {
      let initialize = event.initialize;
      if (
        event.id === 'BUY' ||
        event.id === 'SELL' ||
        event.id === 'COVER' ||
        event.id === 'SHORT' ||
        event.id === 'CONDITIONAL'
      ) {
        const shortPosition = parseFloat(this.quantity) < 0;
        if (this.ticketType === 'option') {
          if (shortPosition) {
            if (event.id === 'BUY' || event.id === 'COVER') {
              this.action = 'buyToClose';
            } else if (event.id === 'SELL' || event.id === 'SHORT') {
              if (this.optionType === 'call') {
                this.action = 'sellToOpenCovered';
              } else {
                this.action = 'sellToOpenUncovered';
              }
            }
          } else {
            if (event.id === 'BUY' || event.id === 'COVER') {
              this.action = 'buyToOpen';
            } else if (event.id === 'SELL' || event.id === 'SHORT') {
              if (event.close) {
                this.action = 'sellToClose';
              } else {
                if (this.optionType === 'call') {
                  this.action = 'sellToOpenCovered';
                } else {
                  this.action = 'sellToOpenUncovered';
                }
              }
            }
          }
        } else {
          if (event.id === 'BUY' || event.id === 'COVER') {
            if (shortPosition) {
              this.action = 'buyToCover';
            } else {
              this.action = this.getBuyAction();
            }
          } else if (event.id === 'SELL' || event.id === 'SHORT') {
            if (shortPosition) {
              this.action = 'sellShort';
            } else {
              this.action = this.getSellAction();
            }
          } else if (event.id === 'CONDITIONAL') {
            if (this.isShortAccount) {
              this.action = 'sellShort';
            } else {
              this.action = 'buy';
            }
          }
        }
        initialize = false;
      }
      const firstLeg = this.$store.state.widget[this.wid].legs['firstOrder'];
      let oppositeAction = this.getOppositeAction(firstLeg.action, this.ticketType);
      let sameAction = this.getSameAction(firstLeg.action, this.ticketType);

      if (this.strategyType) {
        // Strategy
        if (initialize && event.id === 'ROLL') {
          if (this.quantity < 0) {
            this.action = 'buyToClose';
          } else if (this.quantity > 0) {
            this.action = 'sellToClose';
          }
        } else if (initialize || event.id === 'TICKET_TYPE_CHANGED') {
          if (this.strategyType === 'rollStrategy') {
            if (this.leg === 'firstOrder') {
              this.action = 'sellToClose';
            } else if (this.leg === 'secondOrder') {
              if (firstLeg.action === 'sellToClose') {
                this.action = 'buyToOpen';
              } else if (firstLeg.action === 'buyToClose') {
                if (firstLeg.optionType === 'call') {
                  this.action = 'sellToOpenCovered';
                } else if (firstLeg.optionType === 'put') {
                  this.action = 'sellToOpenUncovered';
                }
              }
            }
          } else {
            this.action = '';
          }
        }
      } else if (this.conditionalOrderType) {
        // Conditional Order
        if (initialize || event.id === 'TICKET_TYPE_CHANGED') {
          if (this.leg === 'firstOrder') {
            if (this.ticketType === 'stock') {
              this.action = this.getBuyAction();
            } else if (this.ticketType === 'option') {
              this.action = 'buyToOpen';
            }
          } else {
            const settings = this.$store.state.applicationSettings.orders.settings;
            let actionSetting = settings.conditionalOrders[this.conditionalOrderType][this.leg].action;
            if (actionSetting === 'sameFirstOrder') {
              this.action = sameAction;
            } else if (actionSetting === 'oppositeFirstOrder') {
              this.action = oppositeAction;
            } else if (actionSetting === 'alwaysBuy') {
              this.action = this.getSameAction('buy', this.ticketType);
            } else if (actionSetting === 'alwaysSell') {
              this.action = this.getSameAction('sell', this.ticketType);
            }
          }
        }
      } else {
        // Profit/Loss Exits & SPL
        if (
          this.leg === 'firstOrder' &&
          (event.id === 'BUY' ||
            event.id === 'SELL' ||
            event.id === 'COVER' ||
            event.id === 'SHORT' ||
            event.id === 'ACTION_CHANGED' ||
            event.id === 'TICKET_TYPE_CHANGED' ||
            initialize)
        ) {
          if (event.id === 'TICKET_TYPE_CHANGED' || initialize) {
            if (['stock', 'fractional'].includes(this.ticketType)) {
              this.action = this.getBuyAction();
            } else if (this.ticketType === 'option') {
              this.action = 'buyToOpen';
            }
          }
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'secondOrder',
            key: 'action',
            value: oppositeAction
          });
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'thirdOrder',
            key: 'action',
            value: oppositeAction
          });
        }
        if ((this.leg === 'secondOrder' || this.leg === 'thirdOrder') && initialize) {
          this.action = oppositeAction;
        }
      }
      if (this.strategyType) {
        this.emitFidValues();
      }
      this.setActionAndOptions();
    },
    setDurationGoodTil(event) {
      if (event.id === 'DURATION_CHANGED') {
        this.goodTill = null;
      }
      // Set default duration for first leg from settings
      if (
        event.initialize ||
        event.id === 'TICKET_TYPE_CHANGED' ||
        (event.id === 'UNDERLYING_CHANGED' && event.newValue)
      ) {
        if (this.leg === 'firstOrder') {
          if (this.ticketType === 'stock') {
            this.duration = this.$store.state.applicationSettings.orders.settings.stocks.duration;
            this.goodTill = '';
          } else if (this.ticketType === 'option') {
            this.duration = this.$store.state.applicationSettings.orders.settings.options.duration;
            this.goodTill = '';
          }
          if (this.duration === 'dayExt') {
            if (this.priceType !== 'limit' || this.underlyingMarket !== 'US') {
              this.duration = 'day';
            }
          }
        }
      }
      // Copy first leg duration to second and third leg

      if (
        event.initialize ||
        event.id === 'TICKET_TYPE_CHANGED' ||
        (event.id === 'UNDERLYING_CHANGED' && event.newValue) ||
        event.id === 'DURATION_CHANGED' ||
        event.id === 'GOOD_TILL_CHANGED'
      ) {
        if (this.$store.state.widget[this.wid].legs['secondOrder']) {
          if (this.leg === 'firstOrder') {
            this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
              leg: 'secondOrder',
              key: 'duration',
              value: this.duration
            });
            this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
              leg: 'secondOrder',
              key: 'goodTill',
              value: this.goodTill
            });
            if (this.$store.state.widget[this.wid].legs['thirdOrder']) {
              this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
                leg: 'thirdOrder',
                key: 'duration',
                value: this.duration
              });
              this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
                leg: 'thirdOrder',
                key: 'goodTill',
                value: this.goodTill
              });
            }
          } else if (this.leg === 'secondOrder') {
            this.duration = this.$store.state.widget[this.wid].legs['firstOrder'].duration;
            this.goodTill = this.$store.state.widget[this.wid].legs['firstOrder'].goodTill;
          } else if (this.leg === 'thirdOrder') {
            this.duration = this.$store.state.widget[this.wid].legs['firstOrder'].duration;
            this.goodTill = this.$store.state.widget[this.wid].legs['firstOrder'].goodTill;
          }
        }
      }
      if (this.getDurationOptions().filter(durationOption => durationOption.value === this.duration).length === 0) {
        this.duration = 'day';
      }
    },
    getDurationOptions() {
      const priceType = this.$store.state.widget[this.wid].legs['firstOrder'].priceType;
      const market = this.$store.state.widget[this.wid].legs['firstOrder'].underlying.market;

      const namespace = 'white-label.globalLabels.trading.duration.options.short.';
      let durationOptions = [{ value: 'day', text: this.$t(namespace + 'day') }];
      const isValidPriceTypeForGTX = this.isFromOrderStatusModify ? this.duration === 'dayExt' : priceType === 'limit';

      if (this.isShortAccount && this.action === 'shortSell') {
        if (this.ticketType === 'stock' && isValidPriceTypeForGTX && market === 'US') {
          durationOptions.push({ value: 'dayExt', text: this.$t(namespace + 'dayExt') });
        }
        return durationOptions;
      }

      if (this.ticketType !== 'fractional') {
        durationOptions.push({ value: 'gtd', text: this.$t(namespace + 'gtd') });
      }
      if (priceType !== 'market') {
        durationOptions.push({ value: 'gtc', text: this.$t(namespace + 'gtc') });
      }
      if (this.ticketType === 'stock' && isValidPriceTypeForGTX && market === 'US') {
        durationOptions.push({ value: 'dayExt', text: this.$t(namespace + 'dayExt') });
      }
      return durationOptions;
    },
    setExpiryStrikePrice(event) {
      if (event.id === 'TICKET_TYPE_CHANGED' || event.id === 'UNDERLYING_CHANGED') {
        this.strikePrice = '';
        this.expiryGroupId = '';
      } else if (this.strategyType && this.leg === 'firstOrder') {
        if (event.id === 'EXPIRY_DATE_CHANGED') {
          let expiryGroupId1 = this.expiryGroupId;
          let expiryGroupId2 = this.$store.state.widget[this.wid].legs['secondOrder'].expiryGroupId;
          let newGroupId = '';
          switch (this.strategyType) {
            case 'rollStrategy':
            case 'customOptionOption':
              newGroupId = expiryGroupId2;
              break;
            case 'bullCallSpread':
            case 'bearCallSpread':
            case 'bullPutSpread':
            case 'bearPutSpread':
            case 'callBackSpread':
            case 'callRatioSpread':
            case 'putBackSpread':
            case 'putRatioSpread':
            case 'longStraddle':
            case 'longStrangle':
            case 'shortStraddle':
            case 'shortStrangle':
            case 'longSyntheticStock':
            case 'shortSyntheticStock':
              newGroupId = expiryGroupId1;
              break;
            case 'longCallCalendar':
            case 'shortCallCalendar':
            case 'longPutCalendar':
            case 'shortPutCalendar':
            case 'diagonalBullCallSpread':
            case 'diagonalBearCallSpread':
            case 'diagonalBullPutSpread':
            case 'diagonalBearPutSpread':
              {
                let valid = this.expiryDateValidation();
                if (!valid(this.expiryDate, this.$store.state.widget[this.wid].legs['secondOrder'].expiryDate)) {
                  newGroupId = '';
                } else {
                  newGroupId = expiryGroupId2;
                }
              }
              break;
          }
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'secondOrder',
            key: 'expiryGroupId',
            value: newGroupId
          });
        } else if (event.id === 'STRIKE_PRICE_CHANGED' || event.id === 'OPTION_TYPE_CHANGED') {
          let price1 = this.strikePrice;
          let price2 = this.$store.state.widget[this.wid].legs['secondOrder'].strikePrice;
          let newPrice = '';
          switch (this.strategyType) {
            case 'longStraddle':
            case 'shortStraddle':
            case 'longStrangle':
            case 'shortStrangle':
            case 'longSyntheticStock':
            case 'shortSyntheticStock':
            case 'longCallCalendar':
            case 'shortCallCalendar':
            case 'longPutCalendar':
            case 'shortPutCalendar':
            case 'diagonalBullCallSpread':
            case 'diagonalBearCallSpread':
            case 'diagonalBullPutSpread':
            case 'diagonalBearPutSpread':
              newPrice = price1;
              break;
          }
          let valid = this.strikePriceValidation();
          if (!valid(price1, price2)) {
            this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
              leg: 'secondOrder',
              key: 'strikePrice',
              value: newPrice
            });
          }
        }
      } else if (this.strategyType && this.leg === 'secondOrder') {
        if (event.id === 'EXPIRY_DATE_CHANGED') {
          switch (this.strategyType) {
            case 'longCallCalendar':
            case 'shortCallCalendar':
            case 'longPutCalendar':
            case 'shortPutCalendar':
              this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
                leg: 'secondOrder',
                key: 'strikePrice',
                value: this.$store.state.widget[this.wid].legs['firstOrder'].strikePrice
              });
              break;
          }
        }
      }
      this.setExpiryDateOptions();
    },
    strikePriceValidation() {
      if (this.strategyType) {
        let less = (price1, price2) => {
          return Number.parseFloat(price1) < Number.parseFloat(price2);
        };
        let greater = (price1, price2) => {
          return Number.parseFloat(price1) > Number.parseFloat(price2);
        };
        let equal = (price1, price2) => {
          return Number.parseFloat(price1) === Number.parseFloat(price2);
        };
        let action = this.$store.state.widget[this.wid].legs['firstOrder'].action;
        switch (this.strategyType) {
          case 'bullCallSpread':
          case 'bullPutSpread':
          case 'callRatioSpread':
          case 'putBackSpread':
          case 'diagonalBullCallSpread':
          case 'diagonalBullPutSpread':
            switch (action) {
              case 'buyToOpen':
              case 'sellToClose':
                return less;
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
              case 'buyToClose':
                return greater;
            }
            break;
          case 'bearCallSpread':
          case 'bearPutSpread':
          case 'callBackSpread':
          case 'putRatioSpread':
          case 'diagonalBearCallSpread':
          case 'diagonalBearPutSpread':
            switch (action) {
              case 'buyToOpen':
              case 'sellToClose':
                return greater;
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
              case 'buyToClose':
                return less;
            }
            break;
          case 'longStraddle':
          case 'shortStraddle':
            return equal;
          case 'longStrangle':
          case 'shortStrangle':
            {
              let optionType = this.$store.state.widget[this.wid].legs['firstOrder'].optionType;
              if (optionType === 'call') {
                return greater;
              } else if (optionType === 'put') {
                return less;
              }
            }
            break;
          case 'longSyntheticStock':
          case 'shortSyntheticStock':
          case 'longCallCalendar':
          case 'shortCallCalendar':
          case 'longPutCalendar':
          case 'shortPutCalendar':
            return equal;
        }
      }
      return () => {
        return true;
      };
    },
    expiryDateValidation() {
      if (this.strategyType) {
        const trimDate = date => date.split('_')[0];
        let less = (date1, date2) => {
          return moment(trimDate(date1), 'YYYY-MM-DD') < moment(trimDate(date2), 'YYYY-MM-DD');
        };
        let greater = (date1, date2) => {
          return moment(trimDate(date1), 'YYYY-MM-DD') > moment(trimDate(date2), 'YYYY-MM-DD');
        };
        let action = this.$store.state.widget[this.wid].legs['firstOrder'].action;
        switch (this.strategyType) {
          case 'longCallCalendar':
          case 'longPutCalendar':
          case 'diagonalBullCallSpread':
          case 'diagonalBearCallSpread':
          case 'diagonalBullPutSpread':
          case 'diagonalBearPutSpread':
            switch (action) {
              case 'buyToOpen':
              case 'sellToClose':
                return greater;
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
              case 'buyToClose':
                return less;
            }
            break;
          case 'shortCallCalendar':
          case 'shortPutCalendar':
            switch (action) {
              case 'buyToOpen':
              case 'sellToClose':
                return less;
              case 'sellToOpenCovered':
              case 'sellToOpenUncovered':
              case 'buyToClose':
                return greater;
            }
            break;
        }
      }
      return () => {
        return true;
      };
    },
    updateLimit() {
      if (this.limitType.startsWith('bid')) {
        if (this.isBidValid && this.isLimitDelta) {
          if (this.conditionalOrderType) {
            this.limit = this.calcPrice(this.bid, this.limitDelta, this.limitType);
          } else {
            this.limit = this.bid;
          }
          this.limitType = 'none';
          this.limitDelta = '';
        } else if (this.priceType === 'limit' || this.priceType === 'stopLimit') {
          this.limit = this.bid;
        }
      } else if (this.limitType.startsWith('ask')) {
        if (this.isAskValid && this.isLimitDelta) {
          if (this.conditionalOrderType) {
            this.limit = this.calcPrice(this.ask, this.limitDelta, this.limitType);
          } else {
            this.limit = this.ask;
          }
          this.limitType = 'none';
          this.limitDelta = '';
        } else if (this.priceType === 'limit' || this.priceType === 'stopLimit') {
          this.limit = this.ask;
        }
      }
    },
    updateTrigger() {
      if (this.triggerType.startsWith('bid')) {
        if (this.isBidValid && this.isTriggerDelta) {
          this.trigger = this.calcPrice(this.bid, this.triggerDelta, this.triggerType);
          this.triggerType = 'none';
        } else if (this.priceType === 'stopLimit' || this.priceType === 'stopMarket') {
          this.trigger = this.bid;
        }
      } else if (this.triggerType.startsWith('ask')) {
        if (this.isAskValid && this.isTriggerDelta) {
          this.trigger = this.calcPrice(this.ask, this.triggerDelta, this.triggerType);
          this.triggerType = 'none';
        } else if (this.priceType === 'stopLimit' || this.priceType === 'stopMarket') {
          this.trigger = this.ask;
        }
      }
    },
    resetTrigger() {
      this.configureTrigger('set');
    },
    resetLimit() {
      this.configureLimit('set');
    },
    setLimitTrigger(event) {
      // console.log('setLimitTrigger', this.leg, event.id, event.initialize);
      if (event.id === 'UNDERLYING_CHANGED') {
        if (this.priceType !== 'trailingStopMarket' && this.priceType !== 'trailingStopLimit') {
          this.resetTrigger();
          this.resetLimit();
        }
      } else if (event.id === 'PRICE_TYPE_CHANGED') {
        this.setLimitTriggerFromPriceType();
      }
      if (event.id === 'BUY' || event.id === 'SELL' || event.id === 'COVER' || event.id === 'SHORT') {
        if (event.modifier === 'limit') {
          this.configureLimit('set', event.limit);
        } else {
          const buySellAtType = this.$store.state.applicationSettings.orders.settings.stocks.buySellAtType;
          if (this.priceType === 'limit' || this.priceType === 'stopLimit') {
            if (
              (buySellAtType === 'buyAtAsk' && this.isBuyAction(this.action)) ||
              (buySellAtType === 'buyAtBid' && this.isSellAction(this.action))
            ) {
              this.configureLimit('ask$', '');
            } else if (
              (buySellAtType === 'buyAtAsk' && this.isSellAction(this.action)) ||
              (buySellAtType === 'buyAtBid' && this.isBuyAction(this.action))
            ) {
              this.configureLimit('bid$', '');
            }
          }
        }
      } else if (event.initialize || event.id === 'TICKET_TYPE_CHANGED' || event.id === 'QUOTE') {
        this.resetTrigger();
        this.resetLimit();
        if (this.conditionalOrderType) {
          const settings = this.$store.state.applicationSettings.orders.settings.conditionalOrders[
            this.conditionalOrderType
          ][this.leg];
          // Configure limit
          if (this.priceType === 'limit' || this.priceType === 'stopLimit') {
            if (settings.limitPriceType === 'none') {
              this.configureLimit('set', '');
            } else {
              this.configureLimit(settings.limitPriceType + settings.limitPriceUnit, settings.limitPriceValue);
            }
          } else if (this.priceType === 'trailingStopLimit') {
            this.configureLimit('$', settings.limitDeltaValue);
          }
          // Configure trigger
          if (this.priceType === 'stopLimit' || this.priceType === 'stopMarket') {
            if (settings.triggerPriceType === 'none') {
              this.configureTrigger('set', '');
            } else {
              this.configureTrigger(settings.triggerPriceType + settings.triggerPriceUnit, settings.triggerPriceValue);
            }
          } else if (this.priceType === 'trailingStopMarket' || this.priceType === 'trailingStopLimit') {
            this.configureTrigger(settings.triggerDeltaUnit, settings.triggerDeltaValue);
          }
        } else if (this.profitLossExits) {
          let settings;
          if (this.ticketType === 'stock') {
            settings = this.$store.state.applicationSettings.orders.settings.stocks;
          } else if (this.ticketType === 'option') {
            settings = this.$store.state.applicationSettings.orders.settings.options;
          }
          if (this.leg === 'secondOrder') {
            if (settings.attachProfitTakerOrderLimitPriceType !== 'none') {
              this.configureLimit(
                settings.attachProfitTakerOrderLimitPriceType,
                settings.attachProfitTakerOrderLimitPriceValue
              );
            }
          }
          if (this.leg === 'thirdOrder') {
            if (settings.attachStopLossOrderTriggerPriceType !== 'none') {
              this.configureTrigger(
                settings.attachStopLossOrderTriggerPriceType,
                settings.attachStopLossOrderTriggerPriceValue
              );
            }
            if (settings.attachStopLossOrderLimitPriceType !== 'none') {
              this.configureLimit(
                settings.attachStopLossOrderLimitPriceType,
                settings.attachStopLossOrderLimitPriceValue
              );
            }
          }
        } else if (event.initialize) {
          this.setLimitTriggerFromPriceType();
        }
      }
    },
    setLimitTriggerFromPriceType() {
      const toType = type => {
        if (type === '$' || type === '%') {
          return 'set';
        } else {
          return type;
        }
      };
      const toDeltaType = type => {
        if (type === 'bid' || type === 'ask' || type === 'none') {
          return '$';
        } else {
          return type;
        }
      };
      if (this.priceType === 'market') {
        this.resetTrigger();
        this.resetLimit();
      } else if (this.priceType === 'limit') {
        this.resetTrigger();
        this.configureLimit(toType(this.limitType), this.limit);
      } else if (this.priceType === 'stopLimit') {
        this.configureTrigger(toType(this.triggerType), this.trigger);
        this.configureLimit(toType(this.limitType), this.limit);
      } else if (this.priceType === 'stopMarket') {
        this.configureTrigger(toType(this.triggerType), this.trigger);
        this.resetLimit();
      } else if (this.priceType === 'trailingStopMarket') {
        this.configureTrigger(toDeltaType(this.triggerType), this.triggerDelta);
        this.configureLimit('$', '');
      } else if (this.priceType === 'trailingStopLimit') {
        this.configureTrigger(toDeltaType(this.triggerType), this.triggerDelta);
        this.configureLimit('$', this.limitDelta);
      }
    },
    setOptionType(event) {
      if (event.id === 'TICKET_TYPE_CHANGED') {
        if (this.strategyType) {
          if (this.ticketType === 'option') {
            switch (this.strategyType) {
              case 'customStockOption':
              case 'customOptionOption':
              case 'coveredCall':
              case 'bullCallSpread':
              case 'bearCallSpread':
              case 'callRatioSpread':
              case 'callBackSpread':
              case 'rollStrategy':
                this.optionType = 'call';
                break;
              case 'protectedPut':
              case 'bullPutSpread':
              case 'bearPutSpread':
              case 'putBackSpread':
              case 'putRatioSpread':
                this.optionType = 'put';
                break;
              case 'longStraddle':
              case 'shortStraddle':
              case 'longStrangle':
              case 'shortStrangle':
              case 'shortSyntheticStock':
                if (this.leg === 'firstOrder') {
                  this.optionType = 'put';
                } else if (this.leg === 'secondOrder') {
                  this.optionType = 'call';
                }
                break;
              case 'longSyntheticStock':
                if (this.leg === 'firstOrder') {
                  this.optionType = 'call';
                } else if (this.leg === 'secondOrder') {
                  this.optionType = 'put';
                }
                break;
              case 'longCallCalendar':
              case 'shortCallCalendar':
              case 'diagonalBullCallSpread':
              case 'diagonalBearCallSpread':
                this.optionType = 'call';
                break;
              case 'longPutCalendar':
              case 'shortPutCalendar':
              case 'diagonalBullPutSpread':
              case 'diagonalBearPutSpread':
                this.optionType = 'put';
                break;
            }
          }
        } else if (this.ticketType === 'option') {
          this.optionType = 'call';
        }
      } else if (event.id === 'ACTION_CHANGED' && this.strategyType) {
        switch (this.strategyType) {
          case 'longSyntheticStock':
          case 'shortSyntheticStock':
            if (this.leg === 'firstOrder') {
              switch (this.action) {
                case 'buyToOpen':
                case 'sellToClose':
                  this.optionType = this.strategyType === 'longSyntheticStock' ? 'call' : 'put';
                  break;
                case 'buyToClose':
                case 'sellToOpenCovered':
                case 'sellToOpenUncovered':
                  this.optionType = this.strategyType === 'longSyntheticStock' ? 'put' : 'call';
                  break;
              }
              this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
                leg: 'secondOrder',
                key: 'optionType',
                value: this.optionType === 'put' ? 'call' : 'put'
              });
            }
            break;
        }
      } else if (
        event.id === 'OPTION_TYPE_CHANGED' &&
        this.strategyType === 'rollStrategy' &&
        this.leg === 'firstOrder'
      ) {
        this.optionType === 'put' ? 'put' : 'call';
        this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
          leg: 'secondOrder',
          key: 'optionType',
          value: this.optionType
        });
      }
    },
    setPriceType(event) {
      const settings = this.$store.state.applicationSettings.orders.settings;
      if (
        event.initialize ||
        event.id === 'TICKET_TYPE_CHANGED' ||
        event.id === 'BUY' ||
        event.id === 'SELL' ||
        event.id === 'COVER' ||
        event.id === 'SHORT'
      ) {
        if (event.close) {
          // Closing trade
          if (this.leg === 'firstOrder') {
            if (event.modifier === 'OCO' || event.modifier === 'LIMIT') {
              this.priceType = 'limit';
            } else if (event.modifier === 'STOP-LIMIT') {
              this.priceType = 'stopLimit';
            } else {
              if (this.ticketType === 'stock') {
                this.priceType = settings.stocks.defaultPriceType;
              } else if (this.ticketType === 'option') {
                this.priceType = settings.options.defaultPriceType;
              }
            }
          } else if (this.leg === 'secondOrder') {
            this.priceType = 'stopLimit';
          }
        } else if (this.profitLossExits && (this.leg === 'secondOrder' || this.leg === 'thirdOrder')) {
          if (this.leg === 'secondOrder') {
            this.priceType = 'limit';
          } else if (this.leg === 'thirdOrder') {
            this.priceType = 'stopLimit';
          }
        } else if (this.conditionalOrderType && event.initialize) {
          this.priceType = settings.conditionalOrders[this.conditionalOrderType][this.leg].defaultPriceType;
        } else {
          if (this.ticketType === 'stock' && (event.initialize || event.id === 'TICKET_TYPE_CHANGED')) {
            this.priceType = settings.stocks.defaultPriceType;
          } else if (this.ticketType === 'option' && (event.initialize || event.id === 'TICKET_TYPE_CHANGED')) {
            this.priceType = settings.options.defaultPriceType;
          }
        }
      }

      if ((this.conditionalOrderType === 'oco' && this.priceType === 'market') || event.modifier === 'limit') {
        this.priceType = 'limit';
      }

      // Stop limit fix - Specific case
      if (
        event.id === 'UNDERLYING_CHANGED' &&
        this.underlyingMarket === 'US' &&
        settings.options.defaultPriceType === 'stopLimit'
      ) {
        this.priceType = settings.options.defaultPriceType;
      }
      if (event.id === 'UNDERLYING_CHANGED' && this.ticketType === 'stock') {
        this.priceType = settings.stocks.defaultPriceType;
      }
      const priceTypeOptions = this.getPriceTypeOptions();
      if (
        priceTypeOptions.length > 0 &&
        priceTypeOptions.filter(priceTypeOption => priceTypeOption.value === this.priceType).length === 0
      ) {
        this.priceType = priceTypeOptions[0].value;
      }
    },
    getPriceTypeOptions() {
      let priceTypeOptions = [];
      if (this.isFromOrderStatusModify) {
        // For Limit triggered price type alone shouldn't be considered
        if ((this.priceType === 'market' || this.priceType === 'limit') && !this.triggerIndFromOrderStatus) {
          if (
            this.conditionalOrderType === '' ||
            this.conditionalOrderType === 'ota' ||
            (this.conditionalOrderType === 'fto' && this.leg === 'firstOrder')
          ) {
            priceTypeOptions.push({ value: 'market', text: this.i18n('priceTypes.market') });
          }
          priceTypeOptions.push({ value: 'limit', text: this.i18n('priceTypes.limit') });
        } else if (this.priceType === 'trailingStopLimit' || this.priceType === 'trailingStopMarket') {
          priceTypeOptions = [
            { value: 'trailingStopMarket', text: this.i18n('priceTypes.trailingStopMarket') },
            { value: 'trailingStopLimit', text: this.i18n('priceTypes.trailingStopLimit') }
          ];
        } else if (this.priceType === 'stopLimit' || this.priceType === 'stopMarket') {
          priceTypeOptions = [{ value: 'stopLimit', text: this.i18n('priceTypes.stopLimit') }];
          if (this.ticketType !== 'option')
            priceTypeOptions.push({ value: 'stopMarket', text: this.i18n('priceTypes.stopMarket') });
        } else if (
          this.priceType === 'limitTriggered' ||
          this.priceType === 'marketTriggered' ||
          this.triggerIndFromOrderStatus
        ) {
          priceTypeOptions = [{ value: 'limit', text: this.i18n('priceTypes.limit') }];
        }
      } else {
        if (
          this.conditionalOrderType === '' ||
          this.conditionalOrderType === 'ota' ||
          (this.conditionalOrderType === 'fto' && this.leg === 'firstOrder')
        ) {
          priceTypeOptions.push({ value: 'market', text: this.i18n('priceTypes.market') });
        }
        if (this.ticketType === 'stock') {
          priceTypeOptions = priceTypeOptions.concat([
            { value: 'limit', text: this.i18n('priceTypes.limit') },
            { value: 'stopLimit', text: this.i18n('priceTypes.stopLimit') },
            { value: 'stopMarket', text: this.i18n('priceTypes.stopMarket') }
          ]);
          if (!(this.isShortAccount && this.action === 'shortSell')) {
            priceTypeOptions = priceTypeOptions.concat([
              { value: 'trailingStopMarket', text: this.i18n('priceTypes.trailingStopMarket') },
              { value: 'trailingStopLimit', text: this.i18n('priceTypes.trailingStopLimit') }
            ]);
          }
        } else if (this.ticketType === 'option') {
          priceTypeOptions.push({ value: 'limit', text: this.i18n('priceTypes.limit') });
          if (this.underlyingMarket === 'US' || this.isAttachStopLossVisible) {
            priceTypeOptions.push({ value: 'stopLimit', text: this.i18n('priceTypes.stopLimit') });
          }
        }
      }
      return priceTypeOptions;
    },
    setProfitTakerStopLoss(event) {
      if (event.id === 'TICKET_TYPE_CHANGED' && this.profitLossExits) {
        const settings = this.$store.state.applicationSettings.orders.settings;
        if (this.$store.state.widget[this.wid].legs['firstOrder'].ticketType === 'stock') {
          this.profitTaker = settings.stocks.attachProfitTakerOrder;
          this.stopLoss = settings.stocks.attachStopLossOrder;
        } else if (this.$store.state.widget[this.wid].legs['firstOrder'].ticketType === 'option') {
          this.profitTaker = settings.options.attachProfitTakerOrder;
          this.stopLoss = settings.options.attachStopLossOrder;
        }
      }
    },
    setFractionalQuantityType(event) {
      if (this.isFractionalTrade) {
        if (event.initialize) {
          // this.quantity = 0;
        } else if (event.id === 'TICKET_TYPE_CHANGED') {
          if (!this.fractionalQuantityType)
            this.fractionalQuantityType = this.$store.state.applicationSettings.orders.settings.stocks.defaultFractionalType;
        } else if (event.id === 'ACTION_CHANGED') {
          if (this.action === 'sell' && this.fractionalQuantityType !== '#') {
            this.fractionalQuantityType = '#';
            this.quantity = +parseFloat(this.estimatedFractionalQuantity).toFixed(5);
          }
        }
      }
    },
    setQuantity(event) {
      // console.log('setQuantity', this.leg, JSON.stringify(event), this.quantity, this.strategyType);
      const setSecondAndThirdOrderQuantity = quantity => {
        if (this.leg === 'firstOrder') {
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'secondOrder',
            key: 'quantity',
            value: quantity
          });
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'thirdOrder',
            key: 'quantity',
            value: quantity
          });
        }
      };
      if (
        event.initialize ||
        event.id === 'UNDERLYING_CHANGED' ||
        event.id === 'TICKET_TYPE_CHANGED' ||
        event.id === 'ACTION_CHANGED' ||
        event.id === 'BUY' ||
        event.id === 'SELL' ||
        event.id === 'COVER' ||
        event.id === 'SHORT' ||
        event.id === 'ROLL'
      ) {
        if (
          event.initialize &&
          (this.leg === 'secondOrder' || this.leg === 'thirdOrder') &&
          (this.profitLossExits || this.strategyType === 'rollStrategy' || (event.close && event.modifier === 'OCO'))
        ) {
          this.quantity = this.$store.state.widget[this.wid].legs['firstOrder'].quantity;
        } else {
          if (
            event.id === 'BUY' ||
            event.id === 'SELL' ||
            event.id === 'COVER' ||
            event.id === 'SHORT' ||
            event.id === 'ROLL'
          ) {
            if (event.initialize) {
              if (event.close || this.strategyType === 'rollStrategy') {
                this.quantity = Math.abs(this.quantity);
              } else if (this.ticketType === 'stock') {
                this.quantity = this.$store.state.applicationSettings.orders.settings.stocks.defaultOrderQuantity;
              } else if (this.ticketType === 'option') {
                this.quantity = this.$store.state.applicationSettings.orders.settings.options.defaultOrderQuantity;
              }
            }
          } else if (
            event.id !== 'ACTION_CHANGED' &&
            (Number(this.quantity) === 0 ||
              (event.modifier !== 'OCO' && event.modifier !== 'OTA' && event.modifier !== 'FTO') ||
              event.id === 'CONDITIONAL')
          ) {
            if (this.ticketType === 'stock') {
              this.quantity = Number(this.$store.state.applicationSettings.orders.settings.stocks.defaultOrderQuantity);
            } else if (this.isFractionalTrade) {
              if (this.isNotionalOrder) {
                this.quantity = this.defaultNotionalQuantity;
              } else {
                // setting default Fractional Quantity to 0. and then calcualate in setSymbol after ask price is avaialble
                this.quantity = 0;
              }
            } else if (this.ticketType === 'option') {
              this.quantity = Number(
                this.$store.state.applicationSettings.orders.settings.options.defaultOrderQuantity
              );
            }
            if (event.initialize || event.id === 'UNDERLYING_CHANGED') {
              setSecondAndThirdOrderQuantity(this.quantity);
            }
          }
          if (this.strategyType) {
            this.setQuantityByStrategy(event);
          }
        }
      } else if (event.id === 'QUANTITY_CHANGED') {
        if (this.strategyType) {
          this.setQuantityByStrategy(event);
        } else if (this.conditionalOrderType === '') {
          setSecondAndThirdOrderQuantity(this.quantity);
        }
      }
    },
    setQuantityByStrategy(event) {
      let firstOrderQuantity = this.$store.state.widget[this.wid].legs['firstOrder'].quantity;
      let secondOrderQuantity = firstOrderQuantity;
      if (this.$store.state.widget[this.wid].legs['secondOrder']) {
        secondOrderQuantity = this.$store.state.widget[this.wid].legs['secondOrder'].quantity;
      }

      switch (this.strategyType) {
        case 'coveredCall':
        case 'protectedPut':
          firstOrderQuantity = secondOrderQuantity * 100;
          break;
        case 'bullCallSpread':
        case 'bearCallSpread':
        case 'bullPutSpread':
        case 'bearPutSpread':
          secondOrderQuantity = firstOrderQuantity;
          break;
        case 'callBackSpread':
        case 'putBackSpread':
        case 'callRatioSpread':
        case 'putRatioSpread':
          {
            let quantities = this.getVerticalSpreadQuantities(event, firstOrderQuantity, secondOrderQuantity);
            firstOrderQuantity = quantities[0];
            secondOrderQuantity = quantities[1];
          }
          break;
        case 'longStraddle':
        case 'longStrangle':
        case 'shortStraddle':
        case 'shortStrangle':
        case 'longSyntheticStock':
        case 'shortSyntheticStock':
        case 'longCallCalendar':
        case 'shortCallCalendar':
        case 'longPutCalendar':
        case 'shortPutCalendar':
        case 'diagonalBullCallSpread':
        case 'diagonalBearCallSpread':
        case 'diagonalBullPutSpread':
        case 'diagonalBearPutSpread':
          secondOrderQuantity = firstOrderQuantity;
          break;
      }
      this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
        leg: 'firstOrder',
        key: 'quantity',
        value: firstOrderQuantity
      });
      this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
        leg: 'secondOrder',
        key: 'quantity',
        value: secondOrderQuantity
      });
    },
    getVerticalSpreadQuantities(event, q1, q2) {
      let action = this.$store.state.widget[this.wid].legs['firstOrder'].action;
      let oldAction = event.id === 'ACTION_CHANGED' ? event.oldValue : '';
      switch (this.strategyType) {
        case 'callBackSpread':
        case 'putBackSpread':
          switch (action) {
            case 'buyToOpen':
            case 'sellToClose':
              if (
                event.id === 'TICKET_TYPE_CHANGED' ||
                event.id === 'QUANTITY_CHANGED' ||
                oldAction === 'buyToOpen' ||
                oldAction === 'sellToClose'
              ) {
                q1 = q2 * 2;
              } else if (event.id === 'ACTION_CHANGED') {
                let help = q1;
                q1 = q2;
                q2 = help;
              }
              break;
            case 'sellToOpenCovered':
            case 'sellToOpenUncovered':
            case 'buyToClose':
              if (
                event.id === 'TICKET_TYPE_CHANGED' ||
                event.id === 'QUANTITY_CHANGED' ||
                oldAction === 'sellToOpenCovered' ||
                oldAction === 'sellToOpenUncovered' ||
                oldAction === 'buyToClose'
              ) {
                q2 = q1 * 2;
              } else if (event.id === 'ACTION_CHANGED') {
                let help = q1;
                q1 = q2;
                q2 = help;
              }
              break;
          }
          break;
        case 'callRatioSpread':
        case 'putRatioSpread':
          switch (action) {
            case 'buyToOpen':
            case 'sellToClose':
              if (
                event.id === 'TICKET_TYPE_CHANGED' ||
                event.id === 'QUANTITY_CHANGED' ||
                oldAction === 'buyToOpen' ||
                oldAction === 'sellToClose'
              ) {
                q2 = q1 * 2;
              } else if (event.id === 'ACTION_CHANGED') {
                let help = q1;
                q1 = q2;
                q2 = help;
              }
              break;
            case 'sellToOpenCovered':
            case 'sellToOpenUncovered':
            case 'buyToClose':
              if (
                event.id === 'TICKET_TYPE_CHANGED' ||
                event.id === 'QUANTITY_CHANGED' ||
                oldAction === 'sellToOpenCovered' ||
                oldAction === 'sellToOpenUncovered' ||
                oldAction === 'buyToClose'
              ) {
                q1 = q2 * 2;
              } else if (event.id === 'ACTION_CHANGED') {
                let help = q1;
                q1 = q2;
                q2 = help;
              }
              break;
          }
          break;
      }
      return [q1, q2];
    },
    setSymbol(event) {
      // Symbol / Underlying
      let symbolRic = this.symbolRic;
      let underlyingRic = this.underlyingRic;
      if (event.initialize || event.id === 'UNDERLYING_CHANGED' || event.id === 'TICKET_TYPE_CHANGED') {
        if (
          ((event.initialize && this.strategyType) ||
            (event.id === 'TICKET_TYPE_CHANGED' && event.newValue === 'strategy')) &&
          this.leg === 'secondOrder'
        ) {
          underlyingRic = this.$store.state.widget[this.wid].legs['firstOrder'].underlying.ric;
          this.options = null;
        }
        if (this.ticketType === 'stock' || this.ticketType === 'fractional') {
          symbolRic = underlyingRic;
        } else if (this.ticketType === 'option') {
          symbolRic = '';
        }
      }
      if (this.conditionalOrderType && (this.leg === 'secondOrder' || this.leg === 'thirdOrder')) {
        if (event.initialize || event.id === 'CONDITIONAL_ORDER_TYPE') {
          if (event.close && event.modifier === 'OCO') {
            symbolRic = underlyingRic = this.$store.state.widget[this.wid].legs['firstOrder'].underlying.ric;
          } else {
            const settings = this.$store.state.applicationSettings.orders.settings;
            let symbolSetting = settings.conditionalOrders[this.conditionalOrderType][this.leg].symbol;
            if (symbolSetting === 'sameFirstOrder') {
              underlyingRic = this.$store.state.widget[this.wid].legs['firstOrder'].underlying.ric;
              if (this.ticketType === 'stock') {
                symbolRic = underlyingRic;
              }
            } else {
              underlyingRic = '';
              symbolRic = '';
            }
          }
        }
      }
      let profitLossExits = this.$store.state.widget[this.wid].profitLossExits;
      if (profitLossExits && (event.initialize || event.id === 'UNDERLYING_CHANGED')) {
        underlyingRic = this.$store.state.widget[this.wid].legs['firstOrder'].underlying.ric;
        if (this.leg === 'firstOrder') {
          this.$store.commit(`widget/${this.wid}/SET_UNDERLYING_VALUE`, {
            leg: 'secondOrder',
            key: 'ric',
            value: underlyingRic
          });
          this.$store.commit(`widget/${this.wid}/SET_UNDERLYING_VALUE`, {
            leg: 'thirdOrder',
            key: 'ric',
            value: underlyingRic
          });
        } else {
          symbolRic = underlyingRic;
        }
      }

      if (underlyingRic) {
        this.underlyingRic = underlyingRic;
      } else {
        this.clearUnderlying();
        this.options = null;
      }
      this.registerSymbol(symbolRic).then(() => {
        if (!this.quantity && event.id !== 'QUANTITY_CHANGED') {
          if (this.isFractionalTrade && !this.isNotionalOrder) {
            const base = Number.parseFloat(this.ask);
            if (this.defaultNotionalQuantity > 0 && base > 0) {
              this.quantity = (this.defaultNotionalQuantity / base).toFixed(5);
            }
          }
        }
      });
    },
    setTicketType(event) {
      // Ticket / Strategy Type
      if (event.initialize || event.id === 'TICKET_TYPE_CHANGED') {
        // console.log(
        //   'setTicketType leg=',
        //   this.leg,
        //   'event=',
        //   event.id,
        //   'ticketType=',
        //   this.ticketType,
        //   'conditionalOrders=',
        //   this.conditionalOrders,
        //   'conditionalOrderType=',
        //   this.conditionalOrderType,
        //   'profitLossExits=',
        //   this.profitLossExits,
        //   'profitTaker=',
        //   this.profitTaker,
        //   'stopLoss=',
        //   this.stopLoss,
        //   'strategyType=',
        //   this.strategyType
        // );
        if (this.strategyType) {
          if (
            this.leg === 'firstOrder' &&
            (this.strategyType === 'customStockOption' ||
              this.strategyType === 'coveredCall' ||
              this.strategyType === 'protectedPut')
          ) {
            this.ticketType = 'stock';
          } else {
            this.ticketType = 'option';
          }
        } else if (
          event.initialize &&
          event.id &&
          event.id !== 'BUY' &&
          event.id !== 'SELL' &&
          event.id !== 'COVER' &&
          event.id !== 'SHORT'
        ) {
          this.ticketType = 'stock';
        }
        if (this.$store.state.widget[this.wid].profitLossExits) {
          let ticketType = this.$store.state.widget[this.wid].legs['firstOrder'].ticketType;
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'secondOrder',
            key: 'ticketType',
            value: ticketType
          });
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'thirdOrder',
            key: 'ticketType',
            value: ticketType
          });
        }
      } else if (event.id === 'UNDERLYING_CHANGED' || (event.id === 'CREATED' && !this.isSymbolFractional)) {
        if (this.ticketType === 'fractional') {
          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'firstOrder',
            key: 'ticketType',
            value: 'stock'
          });
          this.quantity = parseInt(this.quantity);
        }
      } else if (event.id === 'CREATED') {
        //Reset the ticket type if fractional trading not allowed
        const isModify = this.$store.state.widget[this.wid].orderMode.action === 'MODIFY';
        if (this.ticketType === 'fractional' && !this.isFractionalTradingAllowed && !isModify) {
          this.ticketType = 'stock';
          this.quantity = parseInt(this.quantity);
        }
      }
    },
    setValues(event) {
      if (
        event.initialize ||
        event.id === 'CREATED' ||
        event.id === 'UNDERLYING_CHANGED' ||
        event.id === 'ACTION_CHANGED' ||
        event.id === 'TICKET_TYPE_CHANGED' ||
        event.id === 'PRICE_TYPE_CHANGED' ||
        event.id === 'QUANTITY_CHANGED' ||
        event.id === 'DURATION_CHANGED' ||
        event.id === 'GOOD_TILL_CHANGED' ||
        event.id === 'BUY' ||
        event.id === 'SELL' ||
        event.id === 'COVER' ||
        event.id === 'SHORT' ||
        event.id === 'ROLL'
      ) {
        // console.log(
        //   'setValues',
        //   'leg=',
        //   this.leg,
        //   'id=',
        //   event.id,
        //   'modifier=',
        //   event.modifier,
        //   'limit=',
        //   event.limit,
        //   'initialize=',
        //   event.initialize,
        //   'conditionalOrderType=',
        //   this.conditionalOrderType,
        //   'profitLossExits=',
        //   this.profitLossExits,
        //   'symbol=',
        //   this.symbol,
        //   'bid=',
        //   this.bid,
        //   'ask=',
        //   this.ask,
        //   'underlying=',
        //   this.underlying,
        //   'ticketType=',
        //   this.ticketType,
        //   'strategyType=',
        //   this.strategyType,
        //   'action=',
        //   this.action,
        //   'quantity=',
        //   this.quantity,
        //   'priceType=',
        //   this.priceType,
        //   'duration=',
        //   this.duration,
        //   'goodTill=',
        //   this.goodTill,
        //   'limit=',
        //   this.limit,
        //   'limitDelta=',
        //   this.limitDelta,
        //   'trigger=',
        //   this.trigger,
        //   'triggerDelta=',
        //   this.triggerDelta,
        //   'optionType=',
        //   this.optionsType,
        //   'options=',
        //   this.options,
        //   'actionOptions=',
        //   this.actionOptions,
        //   'priceTypeOptions=',
        //   this.priceTypeOptions,
        //   'durationOptions=',
        //   this.durationOptions
        // );
        this.setTicketType(event);
        this.setSymbol(event);
        this.setProfitTakerStopLoss(event);
        this.setAction(event);
        this.setFractionalQuantityType(event);
        this.setQuantity(event);
        this.setPriceType(event);
        this.setOptionType(event);
        this.setDurationGoodTil(event);
        this.setLimitTrigger(event);
        this.setExpiryStrikePrice(event);
      }
    },
    registerSymbol(ric) {
      // console.log(
      //   'registerSymbol',
      //   'leg=',
      //   this.leg,
      //   'register=',
      //   ric !== this.symbolRic || this.subscriptionId === null,
      //   'symbolRic:',
      //   this.symbolRic,
      //   '->',
      //   ric,
      //   'underlyingRic=',
      //   this.underlyingRic,
      //   'ticketType=',
      //   this.ticketType,
      //   'subscriptionId=',
      //   this.subscriptionId
      // );
      return new Promise(resolve => {
        if (ric !== this.symbolRic || this.subscriptionId === null) {
          this.subscribing = true;
          this.symbolRic = ric;
          this.symbolTicker = '';
          this.symbolMarket = '';
          this.streamingConnect()
            .then(() => this.streamingUnsubscribe())
            .then(() => this.getOptionChain())
            .then(() => this.getTicker())
            .then(() => this.streamingSubscribe())
            .then(() => {
              this.subscribing = false;
              resolve();
            })
            .catch(error => {
              if (!this.destroyed) {
                this.subscribing = false;
                this.bid = '-';
                this.ask = '-';
                this.emitFidValues();
                this.subscriptionId = null;
                if (this.isSymbolValid) {
                  console.log('Symbol registration failed: symbol=', this.symbolRic, 'error=', error);
                }
              }
            });
        } else {
          this.subscribing = false;
          resolve();
        }
      });
    },
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
        } else {
          resolve();
        }
      });
    },
    streamingSubscribe() {
      return new Promise((resolve, reject) => {
        if (this.isSymbolValid && this.subscriptionId === null) {
          this.subscriptionId = API.Streaming.Quote.subscribe(
            {
              rics: [this.symbolRic],
              fids: streamableFidsFilter(this.fids)
            },
            // Subscription Callback
            (response, error) => {
              if (error) {
                reject(error);
              }
            },
            // Snapshot Callback
            (response, error) => {
              if (error) {
                reject(error);
              } else {
                this.handleQuoteUpdate(response.payload);
                resolve();
              }
            },
            // Update Callback
            (response, error) => {
              if (error) {
                reject(error);
              } else {
                this.handleQuoteUpdate(response.payload);
              }
            }
          );
          // console.log('*** streamingSubscribe leg=', this.leg, 'symbolRic=', this.symbolRic, 'subscriptionId=', this.subscriptionId);
        } else {
          resolve();
        }
      });
    },
    getTicker() {
      return new Promise(resolve => {
        if (this.isSymbolValid) {
          // console.log(
          //   '*** getTicker leg=',
          //   this.leg,
          //   'underlyingRic=',
          //   this.underlyingRic,
          //   'symbolRic=',
          //   this.symbolRic
          // );
          toSymbol([this.underlyingRic, this.symbolRic], true).then(tickers => {
            this.underlyingTicker = tickers[0].ticker.split(':')[0];
            this.underlyingMarket = tickers[0].ticker.split(':')[1];
            this.symbolTicker = tickers[1].ticker.split(':')[0];
            this.symbolMarket = tickers[1].ticker.split(':')[1];
            resolve();
            // console.log(
            //   'getTicker leg=',
            //   this.leg,
            //   'symbolTicker=',
            //   this.symbolTicker,
            //   'symbolMarket=',
            //   this.symbolMarket,
            //   'underlyingTicker=',
            //   this.underlyingTicker,
            //   'underlyingMarket=',
            //   this.underlyingMarket
            // );
          });
        } else {
          resolve();
        }
      });
    },
    streamingUnsubscribe() {
      return new Promise((resolve, reject) => {
        if (!this.unsubscribing && this.subscriptionId !== null) {
          // console.log('*** streamingUnsubscribe leg=', this.leg, 'subscriptionId=', this.subscriptionId);
          this.unsubscribing = true;
          API.Streaming.Quote.unsubscribe(this.subscriptionId, (e, err) => {
            if (!this.destroyed) {
              if (err) {
                reject(err);
              } else {
                this.bid = '-';
                this.ask = '-';
                this.emitFidValues();
                this.subscriptionId = null;
              }
              // console.log('streamingUnsubscribed leg=', this.leg, this.subscriptionId);
              this.unsubscribing = false;
              resolve();
            }
          });
        } else {
          this.unsubscribing = false;
          resolve();
        }
      });
    },
    getCTickFlashClass() {
      const classArray = ['bid-ask-value'];
      if (this.strategyType) classArray.push('strategy');
      else if (this.isFromOrderStatusModify) classArray.push('from-modify');
      return classArray;
    },
    getExpiryType(expiryType) {
      expiryType = expiryType.trim();
      if (expiryType === 'W') {
        return 'W';
      } else if (expiryType === 'M') {
        return 'M';
      } else if (expiryType === 'Q') {
        return 'Q';
      } else {
        return 'M';
      }
    },
    getExpiryTypeFromDisplayName(displayName) {
      let expiryType = 'M';
      if (displayName && displayName !== '-' && displayName.trim() !== '') {
        const parts = displayName.trim().split(' ');
        if (parts.length >= 2) {
          const first = parts[1].charAt(0);
          const last = parts[1].charAt(parts[1].length - 1);
          if (first === '1' || first === '2' || first === '4' || first === '5') {
            expiryType = 'W';
          } else if (last === 'Q') {
            expiryType = 'Q';
          }
        }
      }
      return expiryType;
    },
    setSymbolAfterExpiryDateChange(event) {
      this.symbolRic = '';
      this.registerSymbol(this.getOptionRic()).then(() => {
        this.setExpiryStrikePrice(event);
        this.retrieveOptionDetails();
        // Expiry date should be restricted based on strategy type whenever drop
        // down values are changed.
        this.constrainExpiryDateOptions();
      });
    },
    onFractionalTypeChanged(value) {
      if (value !== this.fractionalQuantityType) {
        this.fractionalQuantityType = value;
        if (value === '$') {
          this.quantity = +parseFloat(this.estimatedNotionalQuantity).toFixed(2);
        } else {
          this.quantity = +parseFloat(this.estimatedFractionalQuantity).toFixed(5);
        }
      }
    },
    onFractionalSymbol(value) {
      this.isSymbolFractional = value;
    }
  },
  watch: {
    legUpdate: {
      handler(value) {
        // console.log(
        //   'watch legUpdate leg=',
        //   this.leg,
        //   'type=',
        //   value.type,
        //   'value=',
        //   value.value,
        //   'ticketType=',
        //   this.ticketType,
        //   'strategyType=',
        //   this.strategyType,
        //   'profitLossExits=',
        //   this.profitLossExits,
        //   'conditionalOrderType=',
        //   this.conditionalOrderType
        // );
        switch (value.type) {
          case 'account':
            this.setAction({ id: 'ACTION_CHANGED', oldValue: this.action, newValue: this.action });
            break;
          case 'ticketType':
            this.setValues({ id: 'TICKET_TYPE_CHANGED', oldValue: this.ticketType, newValue: value.value });
            break;
          case 'strategyType':
            this.setValues({ id: 'TICKET_TYPE_CHANGED', oldValue: this.strategyType, newValue: value.value });
            break;
          case 'profitLossExits':
            if (this.profitLossExits && (this.leg === 'secondOrder' || this.leg === 'thirdOrder')) {
              this.setValues({ initialize: true });
            }
            break;
          case 'conditionalOrders':
          case 'conditionalOrderType':
            if (this.leg === 'firstOrder') {
              this.setPriceType({ id: 'CONDITIONAL_ORDER_TYPE' });
            } else {
              this.setValues({ initialize: true, id: 'CONDITIONAL_ORDER_TYPE' });
            }
            break;
          case 'underlying':
            // Watch change of underlying in strategy where the symbol panel is in WOrderEntryWidget
            this.onUnderlyingChanged(value.value);
            break;
          case 'reregister':
            if (this.leg === 'firstOrder' && this.ticketType === 'stock') {
              this.registerSymbol(this.underlyingRic).then(() => {
                this.setValues({ id: 'SET_SYMBOL' });
              });
            }
            break;
          case 'externalRic':
            this.onExternalRicChanged(value.value);
            break;
          case 'orderMode':
            if (this.leg === 'firstOrder') {
              let action = value.value.action;
              let modifier = value.value.modifier;
              let close = value.value.close;
              const limit = value.value.limit;
              const forceRegister = value.value.reregister; //force register the symbol
              if (this.embedded) {
                if (this.underlyingRic !== this.externalRic || forceRegister) {
                  this.underlyingRic = this.externalRic;
                  this.registerSymbol(this.underlyingRic).then(() => {
                    this.setValues({ id: action, modifier: modifier, limit: limit, initialize: false });
                  });
                } else {
                  this.setValues({ id: action, modifier: modifier, limit: limit, initialize: false });
                }
              } else {
                this.setValues({
                  id: action,
                  modifier: modifier,
                  close: close,
                  initialize: false
                });
              }
            }
            break;
          case 'clear':
            this.setValues({ initialize: true });
            this.clearUnderlying();
            this.onUnderlyingChanged({ ric: '', type: 'select' });
            break;
        }
      },
      deep: true
    },
    isOrderValid: {
      handler(value) {
        this.$emit('order-valid', { valid: value, leg: this.leg });
      }
    },
    currentExpiryGroup: {
      handler(newValue) {
        if (newValue) {
          this.expiryDate = newValue.expiryDate;
        }
      }
    },
    expiryGroupId: {
      handler(newValue, oldValue) {
        if (['stock', 'fractional'].includes(this.ticketType)) {
          return;
        }
        let event = { id: 'EXPIRY_DATE_CHANGED', oldValue: oldValue, newValue: newValue };
        if (!newValue) {
          this.expiryDate = '';
        }
        this.setSymbolAfterExpiryDateChange(event);
      }
    },
    optionType: {
      handler(newValue, oldValue) {
        if (!this.subscribing) {
          let event = { id: 'OPTION_TYPE_CHANGED', oldValue: oldValue, newValue: newValue };
          this.registerSymbol(this.getOptionRic()).then(() => {
            this.setOptionType(event);
            this.setExpiryStrikePrice(event);
          });
        }
      }
    },
    expiryDate: {
      handler(newValue, oldValue) {
        if (['stock', 'fractional'].includes(this.ticketType)) {
          return;
        }
        let event = { id: 'EXPIRY_DATE_CHANGED', oldValue: oldValue, newValue: newValue };
        this.expiryDate = newValue;
        this.symbolRic = '';
        this.registerSymbol(this.getOptionRic()).then(() => {
          this.setExpiryStrikePrice(event);
          this.retrieveOptionDetails();
          // Expiry date should be restricted based on strategy type whenever drop
          // down values are changed.
          this.constrainExpiryDateOptions();
        });
      }
    },
    strikePrice: {
      handler(newValue, oldValue) {
        if (['stock', 'fractional'].includes(this.ticketType)) {
          return;
        }
        let event = { id: 'STRIKE_PRICE_CHANGED', oldValue: oldValue, newValue: newValue };
        this.strikePrice = newValue;
        this.registerSymbol(this.getOptionRic()).then(() => {
          this.setExpiryStrikePrice(event);
          this.constrainExpiryDateOptions();
        });
      }
    },
    firstOrderExpiry: {
      handler() {
        this.constrainExpiryDateOptions();
      }
    },
    firstOrderAction: {
      handler() {
        this.constrainExpiryDateOptions();
      }
    },
    eligible: {
      handler(value) {
        if (value) this.addBlankAction = true;
      }
    }
  }
};
</script>
<style lang="scss">
@import '~@/styles/widgets/order-entry-widget';
</style>
